import { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Base from '../components/Base';
import AuthContext from '../context/AuthContext';
import {
  BarChart3, TrendingUp, ShoppingBag, IndianRupee, Package,
  Eye, Users, Search, ChevronLeft, ChevronRight, Globe, Monitor,
  UserPlus, UserCheck, ArrowDown, Clock, Lightbulb, Download,
  ShoppingCart, AlertTriangle, CheckCircle, Info, XCircle, X,
  Smartphone, Tablet, ArrowUpRight, ArrowDownRight, Star, Repeat,
  Instagram, MessageCircle, Megaphone, UsersRound, HelpCircle,
  Tag, Target, Zap, Heart, Settings, RefreshCw, ChevronDown,
  PlusCircle, Palette, Share2, Award, Shield,
  Layout, UserCog, RotateCcw, ArrowRight, Percent, Store,
  Rocket, MapPin, Briefcase, Gift, Calendar, Copy, ClipboardCheck,
  Sparkles, Trophy, Pin, CircleDot
} from 'lucide-react';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';

// Map backend icon string names to lucide-react components
const ICON_MAP = {
  package: Package, 'shopping-bag': ShoppingBag, 'shopping-cart': ShoppingCart,
  image: Eye, camera: Eye, eye: Eye, tag: Tag, target: Target,
  zap: Zap, heart: Heart, settings: Settings, star: Star, repeat: Repeat,
  'trending-up': TrendingUp, 'bar-chart': BarChart3, users: Users,
  'user-plus': UserPlus, globe: Globe, search: Search, lightbulb: Lightbulb,
  instagram: Instagram, 'message-circle': MessageCircle, megaphone: Megaphone,
  layout: Layout, palette: Palette, shield: Shield, award: Award,
  'alert-triangle': AlertTriangle, 'check-circle': CheckCircle, info: Info,
  'plus-circle': PlusCircle, share: Share2, percent: Percent, store: Store,
  'indian-rupee': IndianRupee, monitor: Monitor, clock: Clock, download: Download,
};
const resolveIcon = (icon) => {
  if (typeof icon === 'function') return icon; // Already a component
  if (typeof icon === 'string') return ICON_MAP[icon] || ICON_MAP[icon.toLowerCase()] || Lightbulb;
  return Lightbulb;
};
// ── Constants ───────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'traffic', label: 'Traffic', icon: Globe },
  { id: 'products', label: 'Products', icon: Eye },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'insights', label: 'Insights', icon: Lightbulb },
];

const COUNTRY_FLAGS = {
  IN: '\ud83c\uddee\ud83c\uddf3', US: '\ud83c\uddfa\ud83c\uddf8', GB: '\ud83c\uddec\ud83c\udde7', CA: '\ud83c\udde8\ud83c\udde6',
  AU: '\ud83c\udde6\ud83c\uddfa', AE: '\ud83c\udde6\ud83c\uddea', SG: '\ud83c\uddf8\ud83c\uddec', DE: '\ud83c\udde9\ud83c\uddea',
  FR: '\ud83c\uddeb\ud83c\uddf7', JP: '\ud83c\uddef\ud83c\uddf5', SA: '\ud83c\uddf8\ud83c\udde6', MY: '\ud83c\uddf2\ud83c\uddfe',
  BD: '\ud83c\udde7\ud83c\udde9', LK: '\ud83c\uddf1\ud83c\uddf0', NP: '\ud83c\uddf3\ud83c\uddf5', KR: '\ud83c\uddf0\ud83c\uddf7',
  IT: '\ud83c\uddee\ud83c\uddf9', ES: '\ud83c\uddea\ud83c\uddf8', NL: '\ud83c\uddf3\ud83c\uddf1', TH: '\ud83c\uddf9\ud83c\udded',
};

// ── Geography Tab Component (Unified — Traffic + Orders + Percentages in one view) ──
const COUNTRY_COLORS = ['#d45614', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#06B6D4'];

const GeographyTab = ({ data, loading }) => {
  if (loading) return <LoadingState text="Loading geography data..." />;
  if (!data) return <EmptyState text="No geography data available yet. Browse your store to generate traffic data." icon={<Globe className="w-12 h-12" />} />;

  const { visitor_geography = [], order_geography = [], split = {}, total_tracked_views = 0, total_all_views = 0, untracked_views = 0 } = data;
  const totalVisitors = visitor_geography.reduce((sum, v) => sum + v.visitors, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <HeroStatCard label="Total Tracked Visits" value={total_tracked_views} icon={<Eye className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" sub={untracked_views > 0 ? `${total_all_views} total (${untracked_views} before tracking)` : undefined} />
        <HeroStatCard label="Countries" value={visitor_geography.length} icon={<Globe className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" />
        <HeroStatCard label="Unique Visitors" value={totalVisitors} icon={<Users className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" />
        <HeroStatCard label="Domestic Orders" value={split.domestic_orders || 0} icon={<Package className="w-5 h-5" />} gradient="from-orange-500 to-red-500" sub={fmt(split.domestic_revenue)} />
        <HeroStatCard label="Intl Orders" value={split.international_orders || 0} icon={<Globe className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" sub={fmt(split.international_revenue)} />
      </div>

      {/* Unified Country Table — Traffic + Orders in one view */}
      <SectionCard title="Traffic & Orders by Country" icon={<Globe className="w-5 h-5 text-[#d45614]" />}>
        <div className="p-6">
          {visitor_geography.length === 0 ? (
            <EmptyState text="No country data yet. New visits will be tracked automatically." icon={<Globe className="w-12 h-12" />} />
          ) : (
            <>
              {/* Visual bars */}
              <div className="space-y-5 mb-8">
                {visitor_geography.map((item, i) => {
                  const flag = COUNTRY_FLAGS[item.country_code] || '\ud83c\udf10';
                  const color = COUNTRY_COLORS[i % COUNTRY_COLORS.length];
                  const orderData = order_geography.find(o => o.country_code === item.country_code);
                  return (
                    <div key={item.country_code} className="group hover:bg-gray-50/50 -mx-3 px-3 py-2 rounded-xl transition-all duration-200">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl w-10 text-center flex-shrink-0">{flag}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5">
                              <span className="text-sm font-bold text-gray-900">{item.country_name}</span>
                              <span className="text-xs px-2.5 py-1 rounded-full font-bold shadow-sm" style={{ backgroundColor: color + '18', color: color, border: `1px solid ${color}25` }}>{item.percentage}%</span>
                            </div>
                            <div className="flex items-center gap-5 text-xs text-gray-500">
                              <span className="flex items-center gap-1"><Eye className="w-3 h-3" /><b className="text-gray-700">{item.views}</b></span>
                              <span className="flex items-center gap-1"><Users className="w-3 h-3" /><b className="text-gray-700">{item.visitors}</b></span>
                              <span className="hidden sm:flex items-center gap-1"><Monitor className="w-3 h-3" /><b className="text-gray-700">{item.sessions}</b></span>
                              {orderData && <span className="text-[#d45614] font-bold">{orderData.orders} orders &middot; {fmt(orderData.revenue)}</span>}
                            </div>
                          </div>
                          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: item.percentage + '%', background: `linear-gradient(90deg, ${color}, ${color}CC)` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Compact data table below */}
              <div className="border-t border-gray-100 pt-5">
                <p className="text-xs text-gray-400 uppercase font-bold tracking-wider mb-4">Detailed Breakdown</p>
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/80">
                        <th className="text-left text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3 pl-4">Country</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Views</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Visitors</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Sessions</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Share</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3">Orders</th>
                        <th className="text-right text-xs text-gray-500 uppercase font-bold tracking-wider pb-3 pt-3 pr-4">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitor_geography.map((item, i) => {
                        const flag = COUNTRY_FLAGS[item.country_code] || '\ud83c\udf10';
                        const orderData = order_geography.find(o => o.country_code === item.country_code);
                        return (
                          <tr key={item.country_code} className="border-t border-gray-50 hover:bg-blue-50/30 transition-colors">
                            <td className="py-3 pl-4"><span className="mr-2">{flag}</span><span className="font-semibold text-gray-800">{item.country_name}</span></td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{item.views}</td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{item.visitors}</td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{item.sessions}</td>
                            <td className="py-3 text-right font-bold tabular-nums" style={{ color: COUNTRY_COLORS[i % COUNTRY_COLORS.length] }}>{item.percentage}%</td>
                            <td className="py-3 text-right text-gray-600 tabular-nums">{orderData ? orderData.orders : '—'}</td>
                            <td className="py-3 text-right font-semibold text-gray-800 pr-4 tabular-nums">{orderData ? fmt(orderData.revenue) : '—'}</td>
                          </tr>
                        );
                      })}
                      {/* Totals row */}
                      <tr className="border-t-2 border-gray-200 bg-gray-50/60 font-bold text-gray-900">
                        <td className="py-3 pl-4">Total</td>
                        <td className="py-3 text-right tabular-nums">{total_tracked_views}</td>
                        <td className="py-3 text-right tabular-nums">{totalVisitors}</td>
                        <td className="py-3 text-right tabular-nums">{visitor_geography.reduce((s, v) => s + v.sessions, 0)}</td>
                        <td className="py-3 text-right tabular-nums">100%</td>
                        <td className="py-3 text-right tabular-nums">{(split.domestic_orders || 0) + (split.international_orders || 0)}</td>
                        <td className="py-3 text-right pr-4 tabular-nums">{fmt((split.domestic_revenue || 0) + (split.international_revenue || 0))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
};

const DAY_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const PAGE_TYPE_LABELS = {
  home: 'Home', shop: 'Shop', product: 'Product', category: 'Category',
  search: 'Search', cart: 'Cart', checkout: 'Checkout', other: 'Other',
};

const PAGE_TYPE_COLORS = {
  home: '#d45614', shop: '#3B82F6', product: '#10B981', category: '#8B5CF6',
  search: '#F59E0B', cart: '#EC4899', checkout: '#6366F1', other: '#9CA3AF',
};

const FUNNEL_COLORS = ['#d45614', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'];
const FUNNEL_LABELS = { home: 'Home', shop: 'Shop', product: 'Product', cart: 'Cart', checkout: 'Checkout' };

const DEVICE_COLORS = { Mobile: '#d45614', Desktop: '#3B82F6', Tablet: '#10B981' };

const ALERT_STYLES = {
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: CheckCircle, iconColor: 'text-emerald-500', glow: 'shadow-emerald-100' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-500', glow: 'shadow-amber-100' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: Info, iconColor: 'text-blue-500', glow: 'shadow-blue-100' },
  danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: XCircle, iconColor: 'text-red-500', glow: 'shadow-red-100' },
};

const fmt = (val) => `\u20B9${Number(val || 0).toLocaleString('en-IN')}`;

// ── CSV Export Utility ─────────────────────────────────────────────────────
const downloadCSV = (headers, rows, filename) => {
  const escape = (val) => {
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

// ── Reusable Components ─────────────────────────────────────────────────────

// Premium Hero Stat Card with gradient icon background
const HeroStatCard = ({ label, value, icon, gradient, sub, growth }) => (
  <div className="group relative bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden">
    {/* Subtle gradient glow in top-right */}
    <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${gradient} opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-300`} />
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-md`}>
          {icon}
        </div>
      </div>
      <p className="text-3xl font-extrabold text-gray-900 font-fell tabular-nums">{value}</p>
      {growth !== undefined && growth !== null && (
        <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-bold ${growth >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
          {growth >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {growth >= 0 ? '+' : ''}{growth}% vs prev
        </div>
      )}
      {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
    </div>
  </div>
);

// Keep original StatCard for backward compatibility within sub-components
const StatCard = ({ label, value, icon, color, bg, sub, growth }) => (
  <HeroStatCard
    label={label}
    value={value}
    icon={icon}
    gradient={
      color === '#d45614' ? 'from-orange-500 to-amber-500' :
      color === '#10B981' ? 'from-emerald-500 to-teal-500' :
      color === '#3B82F6' ? 'from-blue-500 to-cyan-500' :
      color === '#8B5CF6' ? 'from-purple-500 to-indigo-500' :
      'from-gray-500 to-gray-600'
    }
    sub={sub}
    growth={growth}
  />
);

const LoadingState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-24">
    <div className="relative">
      <div className="w-14 h-14 border-4 border-gray-200 rounded-full" />
      <div className="absolute inset-0 w-14 h-14 border-4 border-[#d45614] border-t-transparent rounded-full animate-spin" />
    </div>
    <p className="text-gray-400 mt-5 text-sm font-medium">{text}</p>
  </div>
);

const EmptyState = ({ text, icon }) => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-300">
    {icon && <div className="mb-4 opacity-40">{icon}</div>}
    {!icon && (
      <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
        <BarChart3 className="w-8 h-8 text-gray-200" />
      </div>
    )}
    <p className="text-sm text-gray-400 max-w-xs text-center leading-relaxed">{text}</p>
  </div>
);

const SectionCard = ({ title, icon, children, action }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6 overflow-hidden hover:shadow-md transition-shadow duration-300">
    <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
      <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2.5">
        {icon}
        {title}
      </h2>
      {action}
    </div>
    {children}
  </div>
);

const ExportButton = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 active:scale-95">
    <Download className="w-3.5 h-3.5" />
    Export CSV
  </button>
);

const AlertCard = ({ alert, onDismiss }) => {
  const style = ALERT_STYLES[alert.type] || ALERT_STYLES.info;
  const Icon = style.icon;
  return (
    <div className={`${style.bg} ${style.border} border rounded-2xl p-4 flex items-start gap-3 shadow-sm ${style.glow} hover:shadow-md transition-all duration-200`}>
      <div className={`w-9 h-9 rounded-xl ${style.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${style.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold ${style.text}`}>{alert.title}</p>
        <p className={`text-xs ${style.text} opacity-75 mt-1 leading-relaxed`}>{alert.message}</p>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-gray-300 hover:text-gray-500 shrink-0 p-1 rounded-lg hover:bg-white/50 transition-all">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const ConversionBadge = ({ rate }) => {
  const config = rate >= 20
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : rate >= 10
    ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-red-700 bg-red-50 border-red-200';
  return <span className={`${config} text-xs font-bold px-2.5 py-1 rounded-lg border`}>{rate}%</span>;
};

// ── Donut Chart (pure CSS/SVG) ─────────────────────────────────────────────
const DonutChart = ({ segments, total, size = 160 }) => {
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  let cumulativePercent = 0;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 160 160" className="-rotate-90">
        <circle cx="80" cy="80" r={radius} fill="none" stroke="#f3f4f6" strokeWidth="16" />
        {segments.map((seg, i) => {
          const percent = total > 0 ? seg.count / total : 0;
          const dashLength = percent * circumference;
          const dashOffset = cumulativePercent * circumference;
          cumulativePercent += percent;
          return (
            <circle
              key={i}
              cx="80" cy="80" r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth="16"
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={-dashOffset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-extrabold text-gray-900 font-fell">{total}</span>
        <span className="text-xs text-gray-400 font-medium">Total</span>
      </div>
    </div>
  );
};

// ── Mini Sparkline Bar (for stat cards) ────────────────────────────────────
const MiniBarChart = ({ data, color = '#d45614', height = 32 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-px" style={{ height }}>
      {data.slice(-14).map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-sm opacity-70 hover:opacity-100 transition-opacity"
          style={{ height: `${Math.max((v / max) * 100, 4)}%`, backgroundColor: color, minWidth: 2 }}
        />
      ))}
    </div>
  );
};

// ── Overview Tab ────────────────────────────────────────────────────────────
const OverviewTab = ({ overview, topProducts, statusData, funnel, funnelLoading, comparison, alerts, dismissedAlerts, onDismissAlert }) => {
  const statusTotal = statusData
    ? statusData.pending + statusData.in_progress + statusData.ready + statusData.delivered
    : 0;

  const statusSegments = statusData && statusTotal > 0 ? [
    { label: 'Pending', count: statusData.pending, color: '#F97316' },
    { label: 'In Progress', count: statusData.in_progress, color: '#F59E0B' },
    { label: 'Ready', count: statusData.ready, color: '#3B82F6' },
    { label: 'Delivered', count: statusData.delivered, color: '#10B981' },
  ] : [];

  const funnelMax = funnel ? Math.max(...funnel.map(f => f.sessions), 1) : 1;

  const visibleAlerts = (alerts || []).filter((_, i) => !dismissedAlerts.has(i));
  const g = comparison?.growth;

  // Quick summary
  const curOrders = comparison?.current?.orders || 0;
  const curRevenue = comparison?.current?.revenue || 0;
  const ordersGrowth = g?.orders;

  // Calculate revenue per top product for progress bars
  const maxProductRevenue = topProducts.length > 0
    ? Math.max(...topProducts.map(p => p.revenue || 0), 1) : 1;

  return (
    <>
      {/* Smart Alerts */}
      {visibleAlerts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
          {visibleAlerts.map((alert, idx) => {
            const realIndex = (alerts || []).indexOf(alert);
            return <AlertCard key={realIndex} alert={alert} onDismiss={() => onDismissAlert(realIndex)} />;
          })}
        </div>
      )}

      {/* Quick Summary Banner */}
      {comparison && curOrders > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-r from-[#d45614] to-[#e8722e] rounded-2xl p-5 mb-7 shadow-lg shadow-orange-100">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-12 -mt-12" />
          <div className="absolute bottom-0 left-1/2 w-32 h-32 bg-white/5 rounded-full -mb-16" />
          <div className="relative">
            <p className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-1">Period Summary</p>
            <p className="text-white text-lg font-bold">
              <span className="text-2xl font-extrabold">{curOrders}</span> orders worth <span className="text-2xl font-extrabold">{fmt(curRevenue)}</span>
              {ordersGrowth !== undefined && ordersGrowth !== 0 && (
                <span className={`ml-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${ordersGrowth > 0 ? 'bg-white/20 text-white' : 'bg-red-400/30 text-white'}`}>
                  {ordersGrowth > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {ordersGrowth > 0 ? '+' : ''}{Math.abs(ordersGrowth)}%
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Hero Stat Cards with Growth */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <HeroStatCard label="Total Orders" value={overview?.total_orders || 0} icon={<ShoppingBag className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" sub={overview?.avg_order_value > 0 ? `Avg: ${fmt(overview.avg_order_value)}` : null} growth={g?.orders} />
        <HeroStatCard label="Total Revenue" value={fmt(overview?.total_revenue)} icon={<IndianRupee className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" growth={g?.revenue} />
        <HeroStatCard label="This Month Orders" value={overview?.this_month_orders || 0} icon={<TrendingUp className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" growth={g?.views} />
        <HeroStatCard label="This Month Revenue" value={fmt(overview?.this_month_revenue)} icon={<IndianRupee className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" growth={g?.visitors} />
      </div>

      {/* Two-column: Funnel + Status Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        {/* Conversion Funnel — takes 3 cols */}
        <div className="lg:col-span-3">
          <SectionCard title="Conversion Funnel" icon={<ArrowDown className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6">
              {funnelLoading ? (
                <div className="text-center py-8 text-gray-300 text-sm">Loading funnel...</div>
              ) : funnel && funnel.length > 0 ? (
                <div className="space-y-4">
                  {funnel.map((step, i) => {
                    const prevSessions = i > 0 ? funnel[i - 1].sessions : step.sessions;
                    const dropRate = prevSessions > 0 && i > 0
                      ? Math.round(((prevSessions - step.sessions) / prevSessions) * 100)
                      : null;
                    const widthPercent = Math.max((step.sessions / funnelMax) * 100, 12);
                    return (
                      <div key={step.stage} className="group">
                        <div className="flex items-center gap-4">
                          <span className="w-20 text-sm text-gray-500 text-right shrink-0 font-semibold">
                            {FUNNEL_LABELS[step.stage] || step.stage}
                          </span>
                          <div className="flex-1 relative">
                            <div className="bg-gray-50 rounded-xl h-10 overflow-hidden">
                              <div
                                className="h-full rounded-xl flex items-center px-4 text-white text-xs font-bold transition-all duration-700 ease-out relative overflow-hidden"
                                style={{
                                  width: `${widthPercent}%`,
                                  background: `linear-gradient(135deg, ${FUNNEL_COLORS[i] || '#9CA3AF'}, ${FUNNEL_COLORS[i] || '#9CA3AF'}BB)`,
                                }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0" />
                                <span className="relative">{step.sessions} sessions</span>
                              </div>
                            </div>
                          </div>
                          {dropRate !== null && dropRate > 0 && (
                            <div className="flex items-center gap-1 w-16 justify-end shrink-0">
                              <ArrowDownRight className="w-3 h-3 text-red-400" />
                              <span className="text-xs font-bold text-red-400">-{dropRate}%</span>
                            </div>
                          )}
                          {(dropRate === null || dropRate === 0) && (
                            <div className="w-16 shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState text="No funnel data yet" icon={<ArrowDown className="w-12 h-12" />} />
              )}
            </div>
          </SectionCard>
        </div>

        {/* Orders by Status Donut — takes 2 cols */}
        <div className="lg:col-span-2">
          <SectionCard title="Order Status" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6">
              {statusTotal > 0 ? (
                <div className="flex flex-col items-center">
                  <DonutChart segments={statusSegments} total={statusTotal} size={180} />
                  <div className="grid grid-cols-2 gap-3 mt-6 w-full">
                    {statusSegments.map((seg, i) => (
                      <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 bg-gray-50 rounded-xl">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                        <div className="min-w-0">
                          <span className="text-xs text-gray-500 block">{seg.label}</span>
                          <span className="text-sm font-bold text-gray-900">{seg.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyState text="No order data yet" icon={<ShoppingBag className="w-12 h-12" />} />
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Top Products by Orders — Premium Cards */}
      <SectionCard title="Top Products" icon={<Package className="w-4 h-4 text-[#d45614]" />}>
        {topProducts.length > 0 ? (
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topProducts.slice(0, 6).map((p, i) => (
                <div key={i} className="group relative bg-gray-50/50 hover:bg-white border border-gray-100 hover:border-gray-200 rounded-2xl p-4 hover:shadow-lg transition-all duration-300">
                  {/* Rank badge */}
                  <div className={`absolute -top-2 -left-2 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white shadow-md ${i === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500' : i === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' : i === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400' : 'bg-gray-300'}`}>
                    {i + 1}
                  </div>
                  <div className="flex items-start gap-3.5 mt-1">
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-14 h-14 rounded-xl object-cover shrink-0 bg-gray-100 shadow-sm" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shrink-0 border border-gray-100">
                        <Package className="w-6 h-6 text-gray-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate mb-1">{p.name}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-[#d45614]">{p.orders} orders</span>
                        <span className="text-xs text-gray-400">{fmt(p.revenue)}</span>
                      </div>
                      {/* Revenue progress bar */}
                      <div className="mt-2.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${(p.revenue / maxProductRevenue) * 100}%`,
                            background: 'linear-gradient(90deg, #d45614, #e8722e)',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState text="No order data yet" icon={<Package className="w-12 h-12" />} />
        )}
      </SectionCard>
    </>
  );
};

// ── Traffic Tab ─────────────────────────────────────────────────────────────
const TrafficTab = ({ traffic, referrers, peakHours, devices, loading, geoData, geoLoading }) => {
  if (loading) return <LoadingState text="Loading traffic data..." />;
  if (!traffic) return <EmptyState text="No traffic data yet. Page views will appear here once customers visit your store." icon={<Globe className="w-12 h-12" />} />;

  const maxDaily = Math.max(...(traffic.daily_views || []).map(d => d.views), 1);

  const handleExportTraffic = () => {
    const headers = ['Date', 'Views'];
    const rows = (traffic.daily_views || []).map(d => [d.date, d.views]);
    downloadCSV(headers, rows, 'traffic_data');
  };

  return (
    <>
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <HeroStatCard label="Total Page Views" value={traffic.total_views} icon={<Eye className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" sub={`Last ${traffic.days} days`} />
        <HeroStatCard label="Unique Visitors" value={traffic.unique_visitors} icon={<Users className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" />
        <HeroStatCard label="New Visitors" value={traffic.new_visitors ?? 0} icon={<UserPlus className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" />
        <HeroStatCard label="Returning Visitors" value={traffic.returning_visitors ?? 0} icon={<UserCheck className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" />
      </div>

      {/* Country Traffic Breakdown — full bars + table */}
      {traffic.top_countries && traffic.top_countries.length > 0 && (
        <SectionCard title="Traffic by Country" icon={<Globe className="w-4 h-4 text-[#d45614]" />}>
          <div className="p-6 space-y-5">
            {traffic.top_countries.map((c, i) => {
              const flag = COUNTRY_FLAGS[c.country_code] || '\ud83c\udf10';
              const color = COUNTRY_COLORS[i % COUNTRY_COLORS.length];
              const geoOrders = geoData?.order_geography?.find(o => o.country_code === c.country_code);
              return (
                <div key={c.country_code} className="group hover:bg-gray-50/50 -mx-3 px-3 py-2 rounded-xl transition-all duration-200">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl w-10 text-center flex-shrink-0">{flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2.5">
                          <span className="text-sm font-bold text-gray-900">{c.country_name || c.country_code}</span>
                          <span className="text-xs px-2.5 py-1 rounded-full font-bold shadow-sm" style={{ backgroundColor: color + '18', color: color, border: `1px solid ${color}25` }}>{c.percentage}%</span>
                        </div>
                        <div className="flex items-center gap-5 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /><b className="text-gray-700">{c.count}</b></span>
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" /><b className="text-gray-700">{c.visitors}</b></span>
                          {geoOrders && <span className="text-[#d45614] font-bold">{geoOrders.orders} orders &middot; {fmt(geoOrders.revenue)}</span>}
                        </div>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: c.percentage + '%', background: `linear-gradient(90deg, ${color}, ${color}CC)` }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Domestic vs International summary */}
            {geoData?.split && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 mt-3">
                <div className="relative overflow-hidden p-5 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100">
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-orange-200/20 rounded-full" />
                  <Package className="w-5 h-5 text-[#d45614] mb-2" />
                  <p className="text-3xl font-extrabold text-[#d45614]">{geoData.split.domestic_orders || 0}</p>
                  <p className="text-xs text-gray-500 mt-1 font-medium">Domestic Orders</p>
                  <p className="text-sm font-bold text-gray-800 mt-0.5">{fmt(geoData.split.domestic_revenue)}</p>
                </div>
                <div className="relative overflow-hidden p-5 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-blue-200/20 rounded-full" />
                  <Globe className="w-5 h-5 text-blue-600 mb-2" />
                  <p className="text-3xl font-extrabold text-blue-600">{geoData.split.international_orders || 0}</p>
                  <p className="text-xs text-gray-500 mt-1 font-medium">International Orders</p>
                  <p className="text-sm font-bold text-gray-800 mt-0.5">{fmt(geoData.split.international_revenue)}</p>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Daily Views — Premium Bar Chart */}
      {traffic.daily_views && traffic.daily_views.length > 0 && (
        <SectionCard title="Daily Views" icon={<TrendingUp className="w-4 h-4 text-[#d45614]" />} action={<ExportButton onClick={handleExportTraffic} />}>
          <div className="p-6">
            <div className="flex items-end gap-[3px] h-48">
              {traffic.daily_views.map((d, i) => {
                const heightPct = Math.max((d.views / maxDaily) * 100, 2);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap z-10 shadow-lg pointer-events-none">
                      <div className="text-[10px] text-gray-400">{d.date}</div>
                      <div className="font-bold">{d.views} views</div>
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                    </div>
                    <div
                      className="w-full rounded-t-md transition-all duration-300 group-hover:opacity-80 cursor-pointer"
                      style={{
                        height: `${heightPct}%`,
                        background: `linear-gradient(180deg, #d45614, #d4561488)`,
                        minHeight: '3px',
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-3 text-xs text-gray-400 font-medium">
              <span>{traffic.daily_views[0]?.date}</span>
              {traffic.daily_views.length > 2 && (
                <span>{traffic.daily_views[Math.floor(traffic.daily_views.length / 2)]?.date}</span>
              )}
              <span>{traffic.daily_views[traffic.daily_views.length - 1]?.date}</span>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Views by Page Type + Referrers side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {traffic.by_page_type && traffic.by_page_type.length > 0 && (
          <SectionCard title="Views by Page Type" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6 space-y-4">
              {traffic.by_page_type.map((item, i) => {
                const maxCount = traffic.by_page_type[0]?.count || 1;
                const color = PAGE_TYPE_COLORS[item.page_type] || '#9CA3AF';
                const pct = Math.round((item.count / (traffic.total_views || 1)) * 100);
                return (
                  <div key={i} className="group">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-sm font-semibold text-gray-700">
                          {PAGE_TYPE_LABELS[item.page_type] || item.page_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-400">{pct}%</span>
                        <span className="text-sm font-bold text-gray-900 tabular-nums">{item.count}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${Math.max((item.count / maxCount) * 100, 4)}%`, background: `linear-gradient(90deg, ${color}, ${color}BB)` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {referrers && referrers.length > 0 && (
          <SectionCard title="Traffic Sources" icon={<Globe className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-2">
              {referrers.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-white flex items-center justify-center border border-gray-100 transition-colors">
                      <Globe className="w-4 h-4 text-gray-400" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{r.source}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg">{r.visits}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Device Breakdown — Premium Cards */}
      {devices && devices.length > 0 && (
        <SectionCard title="Device Breakdown" icon={<Monitor className="w-4 h-4 text-[#d45614]" />}>
          <div className="p-6">
            {/* Segmented bar */}
            <div className="flex items-center h-4 rounded-full overflow-hidden mb-6 shadow-inner bg-gray-100">
              {devices.map((d) => (
                <div
                  key={d.device}
                  className="h-full transition-all duration-500 first:rounded-l-full last:rounded-r-full"
                  style={{ width: `${d.percent}%`, backgroundColor: DEVICE_COLORS[d.device] || '#9CA3AF', minWidth: d.percent > 0 ? '8px' : '0' }}
                />
              ))}
            </div>
            {/* Device cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {devices.map((d) => {
                const DeviceIcon = d.device === 'Mobile' ? Smartphone : d.device === 'Tablet' ? Tablet : Monitor;
                const deviceColor = DEVICE_COLORS[d.device] || '#9CA3AF';
                return (
                  <div key={d.device} className="group relative overflow-hidden flex items-center gap-4 p-4 bg-gray-50/80 hover:bg-white border border-transparent hover:border-gray-200 rounded-2xl hover:shadow-lg transition-all duration-300">
                    <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-[0.06]`} style={{ backgroundColor: deviceColor }} />
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: deviceColor + '15' }}>
                      <DeviceIcon className="w-6 h-6" style={{ color: deviceColor }} />
                    </div>
                    <div>
                      <p className="text-base font-bold text-gray-900">{d.device}</p>
                      <p className="text-xs text-gray-500">{d.views} views</p>
                    </div>
                    <div className="ml-auto">
                      <p className="text-2xl font-extrabold tabular-nums" style={{ color: deviceColor }}>{d.percent}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Peak Hours + Day of Week side by side */}
      {peakHours && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Views by Hour" icon={<Clock className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6">
              <div className="flex items-end gap-[2px] h-36">
                {peakHours.hours.map((h) => {
                  const maxH = Math.max(...peakHours.hours.map(x => x.views), 1);
                  const isPeak = h.views === Math.max(...peakHours.hours.map(x => x.views));
                  return (
                    <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap z-10 shadow-lg pointer-events-none">
                        {h.hour}:00 — {h.views} views
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                      </div>
                      <div
                        className="w-full rounded-t transition-all duration-300 cursor-pointer"
                        style={{
                          height: `${Math.max((h.views / maxH) * 100, 3)}%`,
                          background: isPeak ? 'linear-gradient(180deg, #d45614, #d4561488)' : '#e5e7eb',
                          minHeight: '3px',
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-gray-400 font-medium">
                <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Views by Day" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
            <div className="p-6 space-y-3">
              {peakHours.weekdays.map((d) => {
                const maxD = Math.max(...peakHours.weekdays.map(x => x.views), 1);
                const isPeak = d.views === maxD;
                return (
                  <div key={d.day} className="group flex items-center gap-3">
                    <span className="w-10 text-xs text-gray-500 text-right shrink-0 font-semibold">{d.day}</span>
                    <div className="flex-1 bg-gray-50 rounded-lg h-8 overflow-hidden">
                      <div
                        className="h-full rounded-lg flex items-center justify-end pr-3 text-xs font-bold transition-all duration-500"
                        style={{
                          width: `${Math.max((d.views / maxD) * 100, 8)}%`,
                          background: isPeak
                            ? 'linear-gradient(90deg, #d45614, #e8722e)'
                            : 'linear-gradient(90deg, #e5e7eb, #d1d5db)',
                          color: isPeak ? 'white' : '#6b7280',
                        }}
                      >
                        {d.views}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </div>
      )}
    </>
  );
};

// ── Products Tab ────────────────────────────────────────────────────────────
const ProductsTab = ({ topPages, categories, conversion, abandonedCarts, loading }) => {
  if (loading) return <LoadingState text="Loading product data..." />;

  const handleExportProducts = () => {
    if (!conversion || !conversion.length) return;
    const headers = ['Product', 'Views', 'Cart Adds', 'Orders', 'View\u2192Cart %', 'Cart\u2192Order %'];
    const rows = conversion.map(p => [p.name, p.views, p.cart_adds, p.orders, p.view_to_cart, p.cart_to_order]);
    downloadCSV(headers, rows, 'product_conversion');
  };

  return (
    <>
      {/* Product Conversion Funnel */}
      <SectionCard title="Product Conversion Funnel" icon={<ShoppingCart className="w-4 h-4 text-[#d45614]" />} action={conversion && conversion.length > 0 && <ExportButton onClick={handleExportProducts} />}>
        {conversion && conversion.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-4">Product</div>
              <div className="col-span-1 text-right">Views</div>
              <div className="col-span-2 text-right">Cart Adds</div>
              <div className="col-span-1 text-right">Orders</div>
              <div className="col-span-2 text-right">View\u2192Cart</div>
              <div className="col-span-2 text-right">Cart\u2192Order</div>
            </div>
            <div className="divide-y divide-gray-50">
              {conversion.map((p, idx) => (
                <div key={p.product_id} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200 group">
                  <div className="col-span-4 flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-300 w-5">{idx + 1}</span>
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-10 h-10 rounded-xl object-cover shrink-0 bg-gray-100 shadow-sm group-hover:shadow-md transition-shadow" />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shrink-0 border border-gray-100">
                        <Package className="w-4 h-4 text-gray-300" />
                      </div>
                    )}
                    <p className="text-sm font-bold text-gray-900 truncate">{p.name}</p>
                  </div>
                  <div className="col-span-1 text-right"><p className="text-sm text-gray-600 tabular-nums">{p.views}</p></div>
                  <div className="col-span-2 text-right"><p className="text-sm text-gray-600 tabular-nums">{p.cart_adds}</p></div>
                  <div className="col-span-1 text-right"><p className="text-sm font-bold text-gray-900 tabular-nums">{p.orders}</p></div>
                  <div className="col-span-2 text-right"><ConversionBadge rate={p.view_to_cart} /></div>
                  <div className="col-span-2 text-right"><ConversionBadge rate={p.cart_to_order} /></div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No product conversion data yet. Views and cart additions will appear here." icon={<ShoppingCart className="w-12 h-12" />} />
        )}
      </SectionCard>

      {/* Abandoned Carts — Premium Warning */}
      <SectionCard title="Abandoned Carts" icon={<ShoppingCart className="w-4 h-4 text-amber-500" />}>
        {abandonedCarts && abandonedCarts.total_abandoned_carts > 0 ? (
          <>
            <div className="px-6 py-4 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-900">
                    {abandonedCarts.total_abandoned_carts} abandoned carts worth {fmt(abandonedCarts.total_abandoned_value)}
                  </p>
                  <p className="text-xs text-amber-700/70">Recovery opportunity — consider sending follow-up emails</p>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {(abandonedCarts.carts || []).slice(0, 10).map((c) => (
                <div key={c.customer_id} className="px-6 py-4 hover:bg-amber-50/30 transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gray-400" />
                      </div>
                      <div>
                        <span className="text-sm font-bold text-gray-900">{c.customer_name}</span>
                        <span className="text-xs text-gray-400 ml-2">{c.email}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">{fmt(c.total_value)}</p>
                      <p className="text-xs text-gray-400">{c.item_count} item{c.item_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No abandoned carts found." icon={<ShoppingCart className="w-12 h-12" />} />
        )}
      </SectionCard>

      {/* Most Viewed Products */}
      <SectionCard title="Most Viewed Products" icon={<Eye className="w-4 h-4 text-[#d45614]" />}>
        {topPages && topPages.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-1">#</div>
              <div className="col-span-6">Product</div>
              <div className="col-span-3 text-right">Views</div>
              <div className="col-span-2 text-right">Visitors</div>
            </div>
            <div className="divide-y divide-gray-50">
              {topPages.map((p, i) => (
                <div key={p.product_id} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200 group">
                  <div className="col-span-1">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-gradient-to-br from-orange-100 to-amber-100 text-[#d45614]' : 'bg-gray-50 text-gray-400'}`}>{i + 1}</span>
                  </div>
                  <div className="col-span-6 flex items-center gap-3">
                    {p.img ? (
                      <img src={p.img} alt={p.name} className="w-10 h-10 rounded-xl object-cover shrink-0 bg-gray-100 shadow-sm" />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shrink-0 border border-gray-100">
                        <Package className="w-5 h-5 text-gray-300" />
                      </div>
                    )}
                    <p className="text-sm font-bold text-gray-900 truncate">{p.name}</p>
                  </div>
                  <div className="col-span-3 text-right">
                    <p className="text-sm font-bold text-gray-900 tabular-nums">{p.views}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-500 tabular-nums">{p.unique_visitors}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No product view data yet." icon={<Eye className="w-12 h-12" />} />
        )}
      </SectionCard>

      {/* Category Performance */}
      <SectionCard title="Category Performance" icon={<BarChart3 className="w-4 h-4 text-[#d45614]" />}>
        {categories && categories.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-4">Category</div>
              <div className="col-span-2 text-right">Views</div>
              <div className="col-span-2 text-right">Visitors</div>
              <div className="col-span-2 text-right">Orders</div>
              <div className="col-span-2 text-right">Revenue</div>
            </div>
            <div className="divide-y divide-gray-50">
              {categories.map((c, i) => (
                <div key={i} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200">
                  <div className="col-span-4 flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COUNTRY_COLORS[i % COUNTRY_COLORS.length] }} />
                    <p className="text-sm font-bold text-gray-900 truncate">{c.category}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-600 tabular-nums">{c.views}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm text-gray-500 tabular-nums">{c.visitors}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-bold text-gray-900 tabular-nums">{c.orders}</p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-semibold text-[#d45614] tabular-nums">{fmt(c.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyState text="No category data yet." icon={<BarChart3 className="w-12 h-12" />} />
        )}
      </SectionCard>
    </>
  );
};

// ── Customers Tab ───────────────────────────────────────────────────────────
const CustomersTab = ({ authAxios, customerValue }) => {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCustomers = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    try {
      const res = await authAxios.get('/analytics/customers/', { params: { page: p, search: q, page_size: 15 } });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setPage(res.data.page || 1);
    } catch (err) {
      devLog.error('Customer fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchCustomers(1, ''); }, [fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(1, search);
  };

  const summary = customerValue?.summary;
  const topCustomers = customerValue?.top_customers || [];

  const handleExportCustomers = () => {
    if (!topCustomers.length) return;
    const headers = ['Name', 'Email', 'Total Orders', 'Total Spend', 'Avg Order Value', 'First Order', 'Last Order'];
    const rows = topCustomers.map(c => [c.name, c.email, c.total_orders, c.total_spend, c.avg_order_value, c.first_order, c.last_order]);
    downloadCSV(headers, rows, 'customer_value');
  };

  return (
    <>
      {/* LTV Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
          <HeroStatCard label="Total Customers" value={summary.total_customers} icon={<Users className="w-5 h-5" />} gradient="from-orange-500 to-amber-500" />
          <HeroStatCard label="Repeat Buyers" value={summary.repeat_buyers} icon={<Repeat className="w-5 h-5" />} gradient="from-emerald-500 to-teal-500" />
          <HeroStatCard label="Repeat Rate" value={`${summary.repeat_rate}%`} icon={<TrendingUp className="w-5 h-5" />} gradient="from-blue-500 to-cyan-500" />
          <HeroStatCard label="Avg Lifetime Value" value={fmt(summary.avg_ltv)} icon={<Star className="w-5 h-5" />} gradient="from-purple-500 to-indigo-500" />
        </div>
      )}

      {/* Top Customers by Spend */}
      {topCustomers.length > 0 && (
        <SectionCard title="Top Customers by Spend" icon={<Star className="w-4 h-4 text-[#d45614]" />} action={<ExportButton onClick={handleExportCustomers} />}>
          <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-1 text-right">Orders</div>
            <div className="col-span-2 text-right">Total Spend</div>
            <div className="col-span-1 text-right">Avg</div>
            <div className="col-span-2 text-right">Last Order</div>
          </div>
          <div className="divide-y divide-gray-50">
            {topCustomers.map((c, i) => (
              <div key={c.customer_id} className="grid grid-cols-12 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200 group">
                <div className="col-span-3 flex items-center gap-2.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-gradient-to-br from-amber-100 to-orange-100 text-[#d45614]' : 'bg-gray-50 text-gray-400'}`}>{i + 1}</div>
                  <p className="text-sm font-bold text-gray-900 truncate">{c.name}</p>
                </div>
                <div className="col-span-3"><p className="text-sm text-gray-500 truncate">{c.email}</p></div>
                <div className="col-span-1 text-right"><p className="text-sm font-semibold text-gray-700 tabular-nums">{c.total_orders}</p></div>
                <div className="col-span-2 text-right"><p className="text-sm font-bold text-[#d45614] tabular-nums">{fmt(c.total_spend)}</p></div>
                <div className="col-span-1 text-right"><p className="text-xs text-gray-400 tabular-nums">{fmt(c.avg_order_value)}</p></div>
                <div className="col-span-2 text-right">
                  <p className="text-sm text-gray-500">
                    {c.last_order ? new Date(c.last_order).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '\u2014'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Customer List */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-sm font-bold text-gray-900">{total} registered customer{total !== 1 ? 's' : ''}</p>
          <p className="text-xs text-gray-400">Browse and search your customer base</p>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
            <input type="text" placeholder="Search name, email, phone..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-72 pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#d45614]/20 focus:border-[#d45614] transition-all placeholder-gray-300" />
          </div>
          <button type="submit" className="px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white rounded-xl hover:shadow-lg hover:shadow-orange-200 transition-all duration-200 active:scale-95">Search</button>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
        {loading ? <LoadingState text="Loading customers..." /> : customers.length > 0 ? (
          <>
            <div className="hidden sm:grid grid-cols-12 px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="col-span-3">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-2">Phone</div>
              <div className="col-span-2">Joined</div>
              <div className="col-span-1 text-center">Status</div>
            </div>
            <div className="divide-y divide-gray-50">
              {customers.map((c) => (
                <div key={c.id} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-0 items-center px-6 py-4 hover:bg-blue-50/30 transition-all duration-200">
                  <div className="sm:col-span-3 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center text-xs font-bold text-gray-400 border border-gray-100">
                      {(c.username || '?')[0].toUpperCase()}
                    </div>
                    <p className="text-sm font-bold text-gray-900">{c.username}</p>
                  </div>
                  <div className="sm:col-span-4"><p className="text-sm text-gray-500 truncate">{c.email}</p></div>
                  <div className="sm:col-span-2"><p className="text-sm text-gray-500 tabular-nums">{c.phone || '\u2014'}</p></div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-400">
                      {c.date_joined ? new Date(c.date_joined).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '\u2014'}
                    </p>
                  </div>
                  <div className="sm:col-span-1 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${c.is_verified ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.is_verified ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                      {c.is_verified ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 font-medium">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => fetchCustomers(page - 1, search)} disabled={page <= 1}
                    className="p-2 rounded-xl border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => fetchCustomers(page + 1, search)} disabled={page >= totalPages}
                    className="p-2 rounded-xl border border-gray-200 bg-white disabled:opacity-30 hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyState text={search ? `No customers matching "${search}"` : 'No registered customers yet.'} icon={<Users className="w-12 h-12" />} />
        )}
      </div>
    </>
  );
};

// ── Insights Tab — Personalized Growth Dashboard ───────────────────────────

// ── Consultation Flow (3-round adaptive survey) ─────────────────────────────

// Round 1 — About Your Customer (always shown)
const ROUND_1_STEPS = [
  {
    question: 'Who is your typical customer?',
    key: 'customer_age_group',
    options: [
      { value: 'working_women_25_35', label: 'Working women 25-35', icon: Briefcase },
      { value: 'college_students_18_25', label: 'College students 18-25', icon: Users },
      { value: 'brides_families', label: 'Brides & families', icon: Heart },
      { value: 'men', label: 'Men', icon: UserCog },
      { value: 'mixed', label: 'Mixed audience', icon: UsersRound },
    ],
  },
  {
    question: 'What occasion do they buy for?',
    key: 'purchase_occasion',
    options: [
      { value: 'daily_wear', label: 'Daily wear', icon: Star },
      { value: 'office_formal', label: 'Office/formal', icon: Briefcase },
      { value: 'festivals', label: 'Festivals (Diwali/Navratri)', icon: Calendar },
      { value: 'weddings', label: 'Weddings', icon: Gift },
      { value: 'gifting', label: 'Gifting', icon: Gift },
    ],
  },
  {
    question: 'Where are most customers located?',
    key: 'customer_location',
    options: [
      { value: 'same_city', label: 'Same city', icon: MapPin },
      { value: 'same_state', label: 'Same state', icon: MapPin },
      { value: 'pan_india', label: 'Pan-India', icon: Globe },
      { value: 'international', label: 'International', icon: Globe },
    ],
  },
];

// Round 2 — About Your Business (adapts to stage)
const ROUND_2_BY_STAGE = {
  launch: [
    {
      question: 'Have you shared your store link anywhere yet?',
      key: 'primary_channel',
      options: [
        { value: 'instagram_bio', label: 'Instagram bio', icon: Instagram },
        { value: 'whatsapp_status', label: 'WhatsApp status', icon: MessageCircle },
        { value: 'both', label: 'Both', icon: Share2 },
        { value: 'not_yet', label: 'Not yet', icon: HelpCircle },
      ],
    },
    {
      question: 'What type of product photos do you have?',
      key: 'photo_quality',
      options: [
        { value: 'own_photoshoot', label: 'Own photoshoot', icon: Eye },
        { value: 'manufacturer_images', label: 'Manufacturer images', icon: Package },
        { value: 'phone_photos', label: 'Phone photos', icon: Smartphone },
        { value: 'no_photos', label: 'No photos yet', icon: HelpCircle },
      ],
    },
  ],
  traction: [
    {
      question: 'Which marketing effort got you the most sales?',
      key: 'primary_channel',
      options: [
        { value: 'instagram', label: 'Instagram posts', icon: Instagram },
        { value: 'whatsapp', label: 'WhatsApp messages', icon: MessageCircle },
        { value: 'word_of_mouth', label: 'Word of mouth', icon: UsersRound },
        { value: 'paid_ads', label: 'Paid ads', icon: Megaphone },
        { value: 'dont_know', label: "Don't know", icon: HelpCircle },
      ],
    },
    {
      question: 'Do customers message you before buying?',
      key: 'pre_purchase_messaging',
      options: [
        { value: 'always', label: 'Always', icon: MessageCircle },
        { value: 'sometimes', label: 'Sometimes', icon: MessageCircle },
        { value: 'rarely', label: 'Rarely', icon: MessageCircle },
        { value: 'never', label: 'Never', icon: X },
      ],
    },
  ],
  growth: [
    {
      question: "What's stopping you from growing faster?",
      key: 'biggest_challenge',
      options: [
        { value: 'traffic', label: 'Not enough traffic', icon: Eye },
        { value: 'conversion', label: 'Traffic but low conversion', icon: Target },
        { value: 'fulfillment', label: "Can't fulfill more orders", icon: Package },
        { value: 'tools', label: 'Need better tools', icon: Settings },
      ],
    },
    {
      question: 'Do you communicate with customers after purchase?',
      key: 'post_purchase_comms',
      options: [
        { value: 'whatsapp_followup', label: 'WhatsApp follow-up', icon: MessageCircle },
        { value: 'email_updates', label: 'Email updates', icon: Globe },
        { value: 'nothing', label: 'Nothing', icon: X },
        { value: 'want_to', label: "Want to but don't know how", icon: HelpCircle },
      ],
    },
  ],
  scale: [
    {
      question: "What's stopping you from growing faster?",
      key: 'biggest_challenge',
      options: [
        { value: 'traffic', label: 'Not enough traffic', icon: Eye },
        { value: 'conversion', label: 'Traffic but low conversion', icon: Target },
        { value: 'fulfillment', label: "Can't fulfill more orders", icon: Package },
        { value: 'tools', label: 'Need better tools', icon: Settings },
      ],
    },
    {
      question: 'Do you communicate with customers after purchase?',
      key: 'post_purchase_comms',
      options: [
        { value: 'whatsapp_followup', label: 'WhatsApp follow-up', icon: MessageCircle },
        { value: 'email_updates', label: 'Email updates', icon: Globe },
        { value: 'nothing', label: 'Nothing', icon: X },
        { value: 'want_to', label: "Want to but don't know how", icon: HelpCircle },
      ],
    },
  ],
};

// Round 3 — Your Goal (always shown)
const ROUND_3_STEPS = [
  {
    question: 'What would success look like in 3 months?',
    key: 'growth_goal',
    options: [
      { value: 'first_10_orders', label: 'Get my first 10 orders', icon: Package },
      { value: 'double_revenue', label: 'Double my revenue', icon: IndianRupee },
      { value: '100_orders_month', label: 'Reach 100 orders/month', icon: TrendingUp },
      { value: 'expand_categories', label: 'Expand to new categories', icon: PlusCircle },
      { value: 'local_to_national', label: 'Go from local to national', icon: Globe },
    ],
  },
];

// Round metadata
const ROUND_META = [
  { label: 'Round 1: Your Customer', stepCount: 3 },
  { label: 'Round 2: Your Business', stepCount: 2 },
  { label: 'Round 3: Your Goal', stepCount: 1 },
];

// Stage badge configs
const STAGE_CONFIGS = {
  launch: { icon: Rocket, color: '#3B82F6', gradient: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Just Launched', desc: 'Focus on getting your first sale' },
  traction: { icon: TrendingUp, color: '#F59E0B', gradient: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Building Traction', desc: 'Find what works and do more of it' },
  growth: { icon: BarChart3, color: '#10B981', gradient: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Growing Fast', desc: 'Optimize conversion and increase revenue' },
  scale: { icon: Award, color: '#8B5CF6', gradient: 'from-purple-500 to-indigo-500', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Scaling Up', desc: 'Automate, expand, and maximize efficiency' },
};

// Build the full list of steps based on stage
const buildConsultationSteps = (stage) => {
  const effectiveStage = stage && ROUND_2_BY_STAGE[stage] ? stage : 'launch';
  return [
    ...ROUND_1_STEPS,
    ...ROUND_2_BY_STAGE[effectiveStage],
    ...ROUND_3_STEPS,
  ];
};

// Get which round a step index belongs to
const getRoundForStep = (stepIndex) => {
  if (stepIndex < 3) return 0;
  if (stepIndex < 5) return 1;
  return 2;
};

// Stage Badge Component
const StageBadge = ({ stage }) => {
  const config = STAGE_CONFIGS[stage] || STAGE_CONFIGS.launch;
  const Icon = config.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative overflow-hidden ${config.bg} ${config.border} border rounded-2xl p-5 mb-7`}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10" style={{ backgroundColor: config.color }} />
      <div className="absolute bottom-0 left-1/3 w-20 h-20 rounded-full opacity-5" style={{ backgroundColor: config.color }} />
      <div className="relative flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: config.color }}>
            {config.label}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">{config.desc}</p>
        </div>
      </div>
    </motion.div>
  );
};

// Survey Modal Component — 3-round adaptive consultation
const SurveyModal = ({ isOpen, onClose, onSubmit, authAxios, stage }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const steps = useMemo(() => buildConsultationSteps(stage), [stage]);
  const totalSteps = steps.length; // always 6
  const currentRound = getRoundForStep(currentStep);
  const roundMeta = ROUND_META[currentRound];

  if (!isOpen) return null;

  // Show analyzing state
  if (analyzing) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-10 flex flex-col items-center"
        >
          <div className="relative mb-6">
            <div className="w-16 h-16 border-4 border-gray-200 rounded-full" />
            <div className="absolute inset-0 w-16 h-16 border-4 border-[#d45614] border-t-transparent rounded-full animate-spin" />
          </div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg font-bold text-gray-900 mb-2"
          >
            Analyzing your business...
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-sm text-gray-400 text-center"
          >
            Preparing personalized insights based on your answers
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const canProceed = answers[step.key] !== undefined;
  const isLastStep = currentStep === totalSteps - 1;

  const handleSelect = (value) => {
    setAnswers(prev => ({ ...prev, [step.key]: value }));
  };

  const handleNext = async () => {
    if (isLastStep && canProceed) {
      setAnalyzing(true);
      // Show analyzing state for 1.5s to feel like it's computing
      setTimeout(async () => {
        setSubmitting(true);
        try {
          await authAxios.post('/analytics/survey/', answers);
          onSubmit(answers);
        } catch (err) {
          devLog.error('Survey submit error:', err);
          onSubmit(answers);
        } finally {
          setSubmitting(false);
          setAnalyzing(false);
        }
      }, 1500);
    } else if (canProceed) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative overflow-hidden px-6 pt-6 pb-4">
            <div className="absolute -top-16 -right-16 w-40 h-40 bg-gradient-to-br from-[#d45614]/10 to-amber-200/10 rounded-full" />
            <div className="relative flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#d45614] to-[#e8722e] flex items-center justify-center shadow-lg shadow-orange-200">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Business Consultation</p>
                  <p className="text-xs text-gray-400">Step {currentStep + 1} of {totalSteps}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Round label */}
            <div className="flex items-center gap-3 mb-3">
              {ROUND_META.map((rm, i) => {
                const isActive = i === currentRound;
                const isComplete = i < currentRound;
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-300 ${
                      isActive ? 'bg-gradient-to-br from-[#d45614] to-[#e8722e] text-white shadow-sm' :
                      isComplete ? 'bg-emerald-500 text-white' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {isComplete ? <CheckCircle className="w-3 h-3" /> : i + 1}
                    </div>
                    <span className={`text-[11px] font-semibold hidden sm:inline ${isActive ? 'text-[#d45614]' : isComplete ? 'text-emerald-600' : 'text-gray-300'}`}>
                      {rm.label.replace('Round ', 'R').replace(': ', ': ')}
                    </span>
                    {i < ROUND_META.length - 1 && (
                      <div className={`w-4 h-px mx-0.5 ${isComplete ? 'bg-emerald-300' : 'bg-gray-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#d45614] to-[#e8722e]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
          </div>

          {/* Round label badge */}
          <div className="px-6 mb-1">
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-gray-50 text-gray-500 border border-gray-100">
              {roundMeta.label}
            </span>
          </div>

          {/* Question */}
          <div className="px-6 pb-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-5">{step.question}</h3>
                <div className={`grid gap-3 ${step.options.length === 5 ? 'grid-cols-2 sm:grid-cols-3' : step.options.length === 3 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {step.options.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = answers[step.key] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => handleSelect(opt.value)}
                        className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                          isSelected
                            ? 'border-[#d45614] bg-orange-50 shadow-md shadow-orange-100'
                            : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-gradient-to-br from-[#d45614] to-[#e8722e] text-white shadow-md'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className={`text-sm font-semibold ${isSelected ? 'text-[#d45614]' : 'text-gray-700'}`}>
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-5 flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-gray-400 hover:text-gray-600 disabled:opacity-0 disabled:pointer-events-none transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={!canProceed || submitting}
              className={`flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all duration-200 ${
                canProceed
                  ? 'bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white shadow-lg shadow-orange-200 hover:shadow-xl active:scale-95'
                  : 'bg-gray-100 text-gray-300 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : isLastStep ? (
                <>
                  Get My Insights
                  <Zap className="w-4 h-4" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Circular Progress Component for Health Score
const CircularProgress = ({ score, size = 160 }) => {
  const radius = 62;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const color = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
  const bgGlow = score >= 75 ? 'shadow-emerald-100' : score >= 50 ? 'shadow-amber-100' : 'shadow-red-100';

  return (
    <div className={`relative inline-flex items-center justify-center shadow-xl ${bgGlow} rounded-full`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 160 160" className="-rotate-90">
        <circle cx="80" cy="80" r={radius} fill="none" stroke="#f3f4f6" strokeWidth="10" />
        <motion.circle
          cx="80" cy="80" r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-4xl font-extrabold font-fell"
          style={{ color }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
        >
          {score}
        </motion.span>
        <span className="text-xs text-gray-400 font-semibold mt-0.5">out of 100</span>
      </div>
    </div>
  );
};

// Loading skeleton for insights
const InsightsLoadingSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="h-6 bg-gray-100 rounded-lg w-48 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-gray-50 rounded-2xl" />
        ))}
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="h-5 bg-gray-100 rounded-lg w-36 mb-4" />
        <div className="flex justify-center py-6">
          <div className="w-40 h-40 bg-gray-100 rounded-full" />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="h-5 bg-gray-100 rounded-lg w-36 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-10 bg-gray-50 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="h-5 bg-gray-100 rounded-lg w-32 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-28 bg-gray-50 rounded-2xl" />
        ))}
      </div>
    </div>
  </div>
);

// Fallback UI when API is unavailable
const InsightsFallback = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center mb-5 border border-orange-100">
      <Lightbulb className="w-10 h-10 text-[#d45614]/40" />
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">Insights Coming Soon</h3>
    <p className="text-sm text-gray-400 max-w-sm text-center leading-relaxed mb-6">
      We're preparing personalized growth insights for your store. This feature is being set up and will be available shortly.
    </p>
    <button
      onClick={onRetry}
      className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white rounded-xl shadow-lg shadow-orange-200 hover:shadow-xl transition-all active:scale-95"
    >
      <RefreshCw className="w-4 h-4" />
      Try Again
    </button>
  </div>
);

// ── Indian Festival Calendar 2026 ──────────────────────────────────────────
const INDIAN_FESTIVALS_2026 = [
  { name: 'Eid al-Fitr', emoji: '\u2728', date: new Date(2026, 2, 20), action: 'Feature festive & ethnic wear collections', desc: 'Fashion sellers see strong gifting demand' },
  { name: 'Raksha Bandhan', emoji: '\ud83c\udf80', date: new Date(2026, 7, 15), action: 'Promote gifting collections & combos', desc: 'Gifting category sees 2-3x demand' },
  { name: 'Onam', emoji: '\ud83c\udf3e', date: new Date(2026, 8, 6), action: 'Feature Kerala sarees & traditional wear', desc: 'South Indian ethnic wear demand surges' },
  { name: 'Navratri', emoji: '\ud83c\udf1f', date: new Date(2026, 9, 2), endDate: new Date(2026, 9, 11), action: 'Feature festive collection now', desc: 'Fashion sellers see 2-3x orders' },
  { name: 'Dussehra', emoji: '\ud83c\udff9', date: new Date(2026, 9, 12), action: 'Run end-of-festival clearance sale', desc: 'Last chance festive shopping peaks' },
  { name: 'Diwali', emoji: '\ud83e\udea9', date: new Date(2026, 9, 21), action: 'Launch Diwali collection & gift sets', desc: 'Biggest shopping festival of the year' },
  { name: 'Wedding Season Peak', emoji: '\ud83d\udc8d', date: new Date(2026, 10, 1), endDate: new Date(2027, 0, 31), action: 'Stock up on lehengas & sarees', desc: 'Bridal category demand surges' },
  { name: 'Christmas', emoji: '\ud83c\udf84', date: new Date(2026, 11, 25), action: 'Create holiday gift guides & bundles', desc: 'Gifting and party wear demand spikes' },
];

const getUpcomingFestivals = (maxDays = 60, maxCount = 2) => {
  const now = new Date();
  const upcoming = [];
  for (const fest of INDIAN_FESTIVALS_2026) {
    const diff = Math.ceil((fest.date - now) / (1000 * 60 * 60 * 24));
    if (diff > 0 && diff <= maxDays) {
      upcoming.push({ ...fest, daysUntil: diff });
    }
  }
  upcoming.sort((a, b) => a.daysUntil - b.daysUntil);
  return upcoming.slice(0, maxCount);
};

// ── Revenue Forecast Helper ────────────────────────────────────────────────
const calculateRevenueForecast = (weeklyRevenue, growthRate) => {
  if (!weeklyRevenue || weeklyRevenue <= 0) return null;
  const weeklyGrowth = 1 + (growthRate || 0) / 100;
  const weeksInMonth = 4.33;

  // Current trajectory
  let currentMonthly = 0;
  let current3Month = 0;
  for (let w = 1; w <= weeksInMonth; w++) {
    currentMonthly += weeklyRevenue * Math.pow(weeklyGrowth, w);
  }
  for (let w = 1; w <= weeksInMonth * 3; w++) {
    current3Month += weeklyRevenue * Math.pow(weeklyGrowth, w);
  }

  // Optimistic (fix cart abandonment + increase AOV = ~50% boost)
  const optimisticMultiplier = 1.5;
  const optimisticMonthly = currentMonthly * optimisticMultiplier;
  const optimistic3Month = current3Month * optimisticMultiplier;

  return {
    currentMonthly: Math.round(currentMonthly),
    current3Month: Math.round(current3Month),
    optimisticMonthly: Math.round(optimisticMonthly),
    optimistic3Month: Math.round(optimistic3Month),
    growthRate,
  };
};

const InsightsTab = ({ authAxios, alerts, comparison, conversion, abandonedCarts, customerValue, days }) => {
  const navigate = useNavigate();
  const [insightsData, setInsightsData] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsError, setInsightsError] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyAnswers, setSurveyAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem('vasstr_survey_answers');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showAllTips, setShowAllTips] = useState(false);

  // V3: Weekly Focus state
  const [weeklyFocus, setWeeklyFocus] = useState(null);
  const [weeklyFocusLoading, setWeeklyFocusLoading] = useState(true);
  const [completedSteps, setCompletedSteps] = useState(() => {
    try {
      const saved = localStorage.getItem('vasstr_weekly_focus_steps');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // V3: Progress Timeline state
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);

  // V4: Clipboard feedback state
  const [copiedCaption, setCopiedCaption] = useState(false);

  // Fetch insights data — retry on authAxios change (token refresh)
  const fetchInsights = useCallback(async () => {
    setInsightsLoading(true);
    setInsightsError(false);
    try {
      const res = await authAxios.get('/analytics/insights/');
      setInsightsData(res.data);
      if (res.data.survey_completed) {
        setSurveyAnswers(res.data.survey_answers || surveyAnswers);
      }
    } catch (err) {
      devLog.error('Insights fetch error:', err);
      setInsightsError(true);
      buildFallbackInsights();
    } finally {
      setInsightsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // V3: Fetch Weekly Focus data
  useEffect(() => {
    const fetchWeeklyFocus = async () => {
      setWeeklyFocusLoading(true);
      try {
        const res = await authAxios.get('/analytics/weekly-focus/');
        setWeeklyFocus(res.data);
        // Sync completed steps from server
        if (res.data?.steps) {
          const serverCompleted = new Set(
            res.data.steps.filter(s => s.completed).map(s => s.id)
          );
          setCompletedSteps(serverCompleted);
          localStorage.setItem('vasstr_weekly_focus_steps', JSON.stringify([...serverCompleted]));
        }
      } catch (err) {
        if (err?.response?.status !== 404) {
          devLog.error('Weekly focus fetch error:', err);
        }
        // 404 = endpoint doesn't exist yet, build fallback
        buildFallbackWeeklyFocus();
      } finally {
        setWeeklyFocusLoading(false);
      }
    };

    const buildFallbackWeeklyFocus = () => {
      const abandonedCount = abandonedCarts?.total_abandoned_carts || 0;
      const abandonedValue = abandonedCarts?.total_abandoned_value || 0;
      const checkoutCount = comparison?.current?.orders || 0;
      const cartCount = abandonedCount + checkoutCount;

      if (cartCount > 0 && abandonedCount > 0) {
        setWeeklyFocus({
          title: 'Fix your cart abandonment',
          description: `${cartCount} people added items but only ${checkoutCount} checked out. You're losing ~${fmt(abandonedValue)}/week.`,
          total_time: '~20 min total',
          expected_result: `5-8 extra orders (${fmt(Math.round(abandonedValue * 0.2))}-${fmt(Math.round(abandonedValue * 0.3))})`,
          steps: [
            { id: 'create_coupon', label: 'Create a "WELCOME10" coupon', time: '5 min', action_type: 'navigate', action_target: '/my-coupons?auto=WELCOME10&discount=10' },
            { id: 'free_shipping', label: 'Add "Free shipping above \u20B92,999"', time: '5 min', action_type: 'navigate', action_target: '/my-coupons' },
            { id: 'share_coupon', label: 'Share the coupon on Instagram', time: '10 min', action_type: 'copy', action_target: 'coupon_caption' },
          ],
        });
      } else {
        setWeeklyFocus({
          title: 'Get your first sales this week',
          description: 'Your store is set up. Now let\'s drive traffic and convert visitors into buyers.',
          total_time: '~15 min total',
          expected_result: '3-5 store visits from social media',
          steps: [
            { id: 'share_store', label: 'Share your store on Instagram', time: '5 min', action_type: 'copy', action_target: 'store_caption' },
            { id: 'add_products', label: 'Upload at least 5 products', time: '5 min', action_type: 'navigate', action_target: '/UploadProduct' },
            { id: 'create_coupon_first', label: 'Create a welcome coupon', time: '5 min', action_type: 'navigate', action_target: '/my-coupons?auto=WELCOME10&discount=10' },
          ],
        });
      }
    };

    fetchWeeklyFocus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios, abandonedCarts, comparison]);

  // V3: Get progress from insights data OR fetch separately
  useEffect(() => {
    // First try to use progress_history from the main insights response
    if (insightsData?.progress_history && insightsData.progress_history.length > 0) {
      setProgressData({ weeks: insightsData.progress_history });
      setProgressLoading(false);
      return;
    }

    const fetchProgress = async () => {
      setProgressLoading(true);
      try {
        const res = await authAxios.get('/analytics/progress/');
        const history = res.data?.progress_history || res.data?.weeks || [];
        setProgressData({ weeks: history });
      } catch (err) {
        if (err?.response?.status !== 404) {
          devLog.error('Progress fetch error:', err);
        }
        setProgressData(null);
      } finally {
        setProgressLoading(false);
      }
    };
    fetchProgress();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios, insightsData?.progress_history]);

  // V3: Handle step completion
  const handleStepComplete = async (stepId) => {
    const newCompleted = new Set(completedSteps);
    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
    } else {
      newCompleted.add(stepId);
    }
    setCompletedSteps(newCompleted);
    localStorage.setItem('vasstr_weekly_focus_steps', JSON.stringify([...newCompleted]));

    try {
      await authAxios.post('/analytics/weekly-focus/step/', { step_id: stepId });
    } catch (err) {
      // Silently fail for 404 — endpoint may not exist yet
      if (err?.response?.status !== 404) {
        devLog.error('Step complete error:', err);
      }
    }
  };

  // V4: Handle caption copy to clipboard
  const handleCopyCaption = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedCaption(true);
      setTimeout(() => setCopiedCaption(false), 2000);
    }).catch(() => {
      // Fallback for clipboard API failure
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedCaption(true);
      setTimeout(() => setCopiedCaption(false), 2000);
    });
  };

  // Build fallback data from existing props when API is unavailable
  const buildFallbackInsights = useCallback(() => {
    const cur = comparison?.current;
    const prev = comparison?.previous;
    const g = comparison?.growth;
    const totalProducts = conversion?.length || 0;
    const totalOrders = cur?.orders || 0;
    const totalRevenue = cur?.revenue || 0;
    const repeatRate = customerValue?.summary?.repeat_rate || 0;
    const abandonedCount = abandonedCarts?.total_abandoned_carts || 0;
    const abandonedValue = abandonedCarts?.total_abandoned_value || 0;

    // Generate top actions
    const actions = [];
    if (totalProducts < 5) {
      actions.push({
        icon: PlusCircle, title: 'Upload more products',
        description: `You have ${totalProducts} product${totalProducts !== 1 ? 's' : ''}. Stores with 10+ products get 3x more traffic.`,
        cta: 'Upload Product', route: '/UploadProduct', priority: 95,
      });
    }
    if (abandonedCount > 0) {
      actions.push({
        icon: ShoppingCart, title: 'Recover abandoned carts',
        description: `${abandonedCount} cart${abandonedCount !== 1 ? 's' : ''} worth ${fmt(abandonedValue)} left behind. Follow up to recover sales.`,
        cta: 'View Carts', route: null, priority: 90, tabAction: 'products',
      });
    }
    if (repeatRate < 10) {
      actions.push({
        icon: Tag, title: 'Create a coupon',
        description: `Your repeat rate is ${repeatRate}%. A discount coupon can boost return visits by 40%.`,
        cta: 'Create Coupon', route: '/my-coupons', priority: 85,
      });
    }
    if (totalOrders === 0) {
      actions.push({
        icon: Share2, title: 'Share your store',
        description: 'No orders yet this period. Share your store link on social media to drive traffic.',
        cta: 'Go to Template', route: '/TemplateThemes', priority: 88,
      });
    }
    if ((g?.orders || 0) < 0) {
      actions.push({
        icon: Megaphone, title: 'Run a promotion',
        description: `Orders dropped ${Math.abs(g.orders)}% vs last period. A time-limited offer can re-engage customers.`,
        cta: 'Create Coupon', route: '/my-coupons', priority: 82,
      });
    }
    if (totalProducts > 0 && (cur?.views || 0) < totalProducts * 2) {
      actions.push({
        icon: Palette, title: 'Improve your storefront',
        description: `Low traffic — only ${cur?.views || 0} views for ${totalProducts} products. Optimize your template design.`,
        cta: 'Edit Template', route: '/TemplateThemes', priority: 78,
      });
    }
    actions.push({
      icon: UserCog, title: 'Complete your profile',
      description: 'A complete designer profile builds trust. Make sure your bio, logo, and contact info are filled in.',
      cta: 'Edit Profile', route: '/designer-profile', priority: 60,
    });
    actions.push({
      icon: Layout, title: 'Set up SEO',
      description: 'Help customers find you on Google. Add meta titles and descriptions for better search ranking.',
      cta: 'SEO Settings', route: '/seo-settings', priority: 55,
    });

    actions.sort((a, b) => b.priority - a.priority);

    // Build health score
    const healthBreakdown = [
      { label: 'Products', score: Math.min(totalProducts * 4, 20), max: 20, icon: totalProducts >= 5 ? CheckCircle : totalProducts >= 2 ? AlertTriangle : XCircle },
      { label: 'Orders', score: Math.min(totalOrders * 2, 20), max: 20, icon: totalOrders >= 10 ? CheckCircle : totalOrders >= 1 ? AlertTriangle : XCircle },
      { label: 'Template', score: 15, max: 15, icon: CheckCircle },
      { label: 'Profile', score: 10, max: 15, icon: AlertTriangle },
      { label: 'Marketing', score: surveyAnswers?.discount_frequency === 'never' ? 5 : 12, max: 15, icon: surveyAnswers?.discount_frequency !== 'never' ? CheckCircle : AlertTriangle },
      { label: 'Engagement', score: repeatRate > 10 ? 15 : repeatRate > 0 ? 8 : 3, max: 15, icon: repeatRate > 10 ? CheckCircle : repeatRate > 0 ? AlertTriangle : XCircle },
    ];
    const healthScore = healthBreakdown.reduce((s, b) => s + b.score, 0);

    // Build tips
    const tips = [];
    const channelTips = {
      instagram: { icon: Instagram, title: 'Boost your Instagram presence', description: 'Post product reels 3x/week. Use carousel posts for collections. Add your store link to bio.', metric: 'Instagram' },
      whatsapp: { icon: MessageCircle, title: 'Leverage WhatsApp catalogs', description: 'Create a WhatsApp Business catalog. Send new arrivals to your broadcast list weekly.', metric: 'WhatsApp' },
      google: { icon: Search, title: 'Optimize for search', description: 'Add detailed product descriptions with keywords. Set up Google Business Profile for local visibility.', metric: 'SEO' },
      word_of_mouth: { icon: UsersRound, title: 'Launch a referral program', description: 'Offer 10% off for referrals. Happy customers are your best marketers.', metric: 'Referrals' },
    };
    // Growth tips
    tips.push({ category: 'growth', icon: Zap, title: 'Add product photos from multiple angles', description: `Products with 3+ images get 2x more views. ${totalProducts > 0 ? `You have ${totalProducts} products to optimize.` : ''}`, metric: '2x views', cta: 'Upload Product', route: '/UploadProduct' });
    if (totalOrders > 0) {
      tips.push({ category: 'revenue', icon: IndianRupee, title: 'Upsell with product bundles', description: `Your average order could increase by 30%. Create collections of complementary items.`, metric: '+30% AOV', cta: 'View Products', route: '/homepage' });
    }
    tips.push({ category: 'revenue', icon: TrendingUp, title: 'Offer free shipping threshold', description: 'Set a free shipping minimum above your average order value to increase cart size.', metric: 'Revenue', cta: null, route: null });
    // Channel-specific tip
    if (surveyAnswers?.traffic_source && channelTips[surveyAnswers.traffic_source]) {
      const ct = channelTips[surveyAnswers.traffic_source];
      tips.push({ category: 'channel', icon: ct.icon, title: ct.title, description: ct.description, metric: ct.metric, cta: null, route: null });
    }
    tips.push({ category: 'channel', icon: Globe, title: 'Cross-post to multiple platforms', description: 'Share your products on Instagram, WhatsApp status, and Facebook simultaneously.', metric: 'Reach', cta: null, route: null });
    // Conversion tips
    if (abandonedCount > 0) {
      tips.push({ category: 'conversion', icon: ShoppingCart, title: 'Reduce cart abandonment', description: `${abandonedCount} customers left items in cart. Add trust badges and simplify checkout.`, metric: `${abandonedCount} carts`, cta: null, route: null });
    }
    tips.push({ category: 'conversion', icon: Eye, title: 'Write compelling product descriptions', description: 'Include fabric type, measurements, care instructions. Customers buy what they understand.', metric: 'Conversions', cta: null, route: null });
    // Store setup tips
    tips.push({ category: 'setup', icon: Shield, title: 'Display your return policy', description: 'Stores with visible return policies see 17% more conversions. Build buyer confidence.', metric: '+17%', cta: null, route: null });
    tips.push({ category: 'setup', icon: Store, title: 'Add store contact info', description: 'A phone number or WhatsApp link makes customers 2x more likely to order.', metric: '2x trust', cta: 'Edit Profile', route: '/designer-profile' });
    if (repeatRate < 20) {
      tips.push({ category: 'growth', icon: Heart, title: 'Start a loyalty program', description: `Only ${repeatRate}% of customers return. Offer points or credits for repeat purchases.`, metric: `${repeatRate}% repeat`, cta: null, route: null });
    }

    // Build weekly comparison
    const weekly = [
      { label: 'Orders', current: cur?.orders || 0, previous: prev?.orders || 0, growth: g?.orders, prefix: '' },
      { label: 'Revenue', current: cur?.revenue || 0, previous: prev?.revenue || 0, growth: g?.revenue, prefix: '\u20B9', isCurrency: true },
      { label: 'Visitors', current: cur?.visitors || 0, previous: prev?.visitors || 0, growth: g?.visitors, prefix: '' },
      { label: 'Page Views', current: cur?.views || 0, previous: prev?.views || 0, growth: g?.views, prefix: '' },
    ];

    // Determine stage from fallback data
    const fallbackStage = totalOrders >= 50 ? 'scale' : totalOrders >= 10 ? 'growth' : totalOrders >= 1 ? 'traction' : 'launch';

    setInsightsData({
      survey_completed: !!surveyAnswers,
      stage: fallbackStage,
      top_actions: actions.slice(0, 3),
      all_actions: actions,
      health_score: healthScore,
      health_breakdown: healthBreakdown,
      tips,
      weekly_comparison: weekly,
      survey_answers: surveyAnswers,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparison, conversion, abandonedCarts, customerValue, surveyAnswers]);

  // Rebuild fallback data when dependencies change (and API failed)
  useEffect(() => {
    if (insightsError && comparison) {
      buildFallbackInsights();
    }
  }, [insightsError, comparison, buildFallbackInsights]);

  // Show survey modal if no survey answers
  useEffect(() => {
    if (!insightsLoading && !surveyAnswers && !surveyOpen) {
      const timer = setTimeout(() => setSurveyOpen(true), 600);
      return () => clearTimeout(timer);
    }
  }, [insightsLoading, surveyAnswers, surveyOpen]);

  const handleSurveySubmit = (answers) => {
    setSurveyAnswers(answers);
    localStorage.setItem('vasstr_survey_answers', JSON.stringify(answers));
    setSurveyOpen(false);
    // Refetch insights with survey data
    fetchInsights();
  };

  const handleRetakeSurvey = () => {
    setSurveyOpen(true);
  };

  const handleRetry = () => {
    fetchInsights();
  };

  const handleActionClick = (action) => {
    if (action.route) {
      navigate(action.route);
    } else if (action.tabAction) {
      // Switch to another analytics tab — can't navigate directly, fallback to scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Derived data — map API field names to UI expectations
  const data = insightsData;
  const topActions = data?.top_actions || [];
  const healthScore = data?.store_health_score ?? data?.health_score ?? 0;
  const healthBreakdown = data?.store_health_breakdown ?? data?.health_breakdown ?? [];
  const allTips = data?.insights ?? data?.tips ?? [];

  // Normalize weekly comparison: API returns {this_week, last_week, change}, fallback returns array
  const rawComparison = data?.weekly_comparison;
  const weeklyComparison = Array.isArray(rawComparison) ? rawComparison
    : (rawComparison?.this_week ? [
        { label: 'Orders', current: rawComparison.this_week.orders || 0, previous: rawComparison.last_week?.orders || 0, growth: rawComparison.change?.orders, prefix: '' },
        { label: 'Revenue', current: rawComparison.this_week.revenue || 0, previous: rawComparison.last_week?.revenue || 0, growth: rawComparison.change?.revenue, prefix: '\u20B9', isCurrency: true },
        { label: 'Visitors', current: rawComparison.this_week.visitors || 0, previous: rawComparison.last_week?.visitors || 0, growth: rawComparison.change?.visitors, prefix: '' },
        { label: 'Page Views', current: rawComparison.this_week.pageviews || 0, previous: rawComparison.last_week?.pageviews || 0, growth: rawComparison.change?.pageviews, prefix: '' },
      ] : []);

  const visibleTips = showAllTips ? allTips : allTips.slice(0, 8);

  const tipCategoryConfig = {
    growth: { label: 'Growth', color: '#d45614', bg: 'bg-orange-50', border: 'border-orange-100' },
    revenue: { label: 'Revenue', color: '#10B981', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    channel: { label: 'Channel', color: '#3B82F6', bg: 'bg-blue-50', border: 'border-blue-100' },
    conversion: { label: 'Conversion', color: '#8B5CF6', bg: 'bg-purple-50', border: 'border-purple-100' },
    setup: { label: 'Store Setup', color: '#F59E0B', bg: 'bg-amber-50', border: 'border-amber-100' },
  };

  if (insightsLoading && !data) return <InsightsLoadingSkeleton />;
  if (insightsError && !data) return <InsightsFallback onRetry={handleRetry} />;

  return (
    <>
      {/* Survey Modal */}
      <SurveyModal
        isOpen={surveyOpen}
        onClose={() => setSurveyOpen(false)}
        onSubmit={handleSurveySubmit}
        authAxios={authAxios}
        stage={data?.stage}
      />

      {/* Stage Badge */}
      {surveyAnswers && (data?.stage || insightsError) && (
        <StageBadge stage={data?.stage || 'launch'} />
      )}

      {/* V3: Weekly Focus Card — HERO section */}
      {weeklyFocus && !weeklyFocusLoading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-7"
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
            {/* Warm orange gradient header */}
            <div className="relative overflow-hidden px-6 py-5 bg-gradient-to-r from-orange-500 via-[#d45614] to-amber-600">
              <div className="absolute top-0 right-0 w-56 h-56 bg-white/5 rounded-full -mr-20 -mt-20" />
              <div className="absolute bottom-0 left-1/4 w-28 h-28 bg-white/5 rounded-full -mb-14" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Pin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white">This Week's Focus</h2>
                    <p className="text-xs text-white/70 mt-0.5">Your personalized action plan</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-white/80 bg-white/15 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                  {weeklyFocus.total_time || '~20 min total'}
                </span>
              </div>
            </div>

            <div className="p-6">
              {/* All steps completed celebration */}
              {weeklyFocus.steps && weeklyFocus.steps.length > 0 && weeklyFocus.steps.every(s => completedSteps.has(s.id)) ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <div className="text-5xl mb-4">{'\ud83c\udf89'}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">All done! Great work!</h3>
                  <p className="text-sm text-gray-500">Check back next week for results and your new focus area.</p>
                </motion.div>
              ) : (
                <>
                  {/* Focus description */}
                  <div className="mb-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{weeklyFocus.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{weeklyFocus.description}</p>
                  </div>

                  {/* Steps */}
                  <div className="space-y-3 mb-5">
                    {(weeklyFocus.steps || []).map((step, i) => {
                      const isCompleted = completedSteps.has(step.id);
                      return (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className={`relative rounded-xl border-2 p-4 transition-all duration-300 ${
                            isCompleted
                              ? 'border-emerald-200 bg-emerald-50/50'
                              : 'border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Checkbox */}
                              <button
                                onClick={() => handleStepComplete(step.id)}
                                className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                                  isCompleted
                                    ? 'border-emerald-500 bg-emerald-500'
                                    : 'border-gray-300 hover:border-[#d45614]'
                                }`}
                              >
                                {isCompleted && <CheckCircle className="w-4 h-4 text-white" />}
                              </button>

                              {/* Step label */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                    Step {i + 1} ({step.time || (step.time_minutes ? step.time_minutes + ' min' : '')})
                                  </span>
                                </div>
                                <p className={`text-sm font-semibold mt-0.5 transition-all ${
                                  isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'
                                }`}>
                                  {step.title || step.label}
                                </p>
                              </div>
                            </div>

                            {/* Action button */}
                            {!isCompleted && (() => {
                              const link = step.cta_link || step.action_target;
                              const actionType = step.action_type || (link ? 'navigate' : null);
                              const isCopy = actionType === 'copy';
                              return (
                                <button
                                  onClick={() => {
                                    if (isCopy) {
                                      handleCopyCaption(`New arrivals! Use code WELCOME10 for 10% off. Shop now! #fashion #ethnic #indianwear`);
                                    } else if (link) {
                                      navigate(link);
                                    }
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#d45614] bg-orange-50 border border-orange-100 rounded-lg hover:bg-orange-100 transition-all active:scale-95 flex-shrink-0"
                                >
                                  {isCopy ? (
                                    copiedCaption ? <><ClipboardCheck className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>
                                  ) : (
                                    <>{step.cta_text || 'Do it'} <ArrowRight className="w-3 h-3" /></>
                                  )}
                                </button>
                              );
                            })()}

                            {isCompleted && (
                              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                                <CheckCircle className="w-3.5 h-3.5" /> Done
                              </span>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Expected result */}
                  {weeklyFocus.expected_result && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                      <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <p className="text-xs font-bold text-emerald-800">
                        EXPECTED: {weeklyFocus.expected_result}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Section 1: Top 3 Actions This Week */}
      {topActions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-7"
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
            {/* Gradient Header */}
            <div className="relative overflow-hidden px-6 py-5 bg-gradient-to-r from-[#d45614] to-[#e8722e]">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-16 -mt-16" />
              <div className="absolute bottom-0 left-1/3 w-24 h-24 bg-white/5 rounded-full -mb-12" />
              <div className="relative flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">Your Top Actions This Week</h2>
                  <p className="text-xs text-white/70 mt-0.5">Focus on these to grow your store</p>
                </div>
              </div>
            </div>
            {/* Actions */}
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {topActions.map((action, i) => {
                  const Icon = resolveIcon(action.icon);
                  const colors = [
                    { gradient: 'from-[#d45614] to-[#e8722e]', light: 'bg-orange-50', border: 'border-orange-100', text: 'text-[#d45614]' },
                    { gradient: 'from-blue-500 to-cyan-500', light: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-600' },
                    { gradient: 'from-emerald-500 to-teal-500', light: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-600' },
                  ];
                  const c = colors[i % colors.length];
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 + 0.2 }}
                      className={`group relative overflow-hidden p-5 ${c.light} border ${c.border} rounded-2xl hover:shadow-lg transition-all duration-300`}
                    >
                      <div className="absolute -top-6 -right-6 w-20 h-20 bg-white/30 rounded-full" />
                      <div className="relative">
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br ${c.gradient} text-white text-xs font-bold shadow-sm`}>
                            {i + 1}
                          </span>
                          <div className={`w-8 h-8 rounded-lg ${c.light} flex items-center justify-center`}>
                            <Icon className={`w-4 h-4 ${c.text}`} />
                          </div>
                        </div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{action.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{action.description}</p>
                        <button
                          onClick={() => handleActionClick(action)}
                          className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold ${c.text} ${c.light} border ${c.border} rounded-xl hover:shadow-sm transition-all active:scale-95`}
                        >
                          {action.cta}
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Section 2: Store Health Score + Weekly Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-7">
        {/* Health Score — 2 cols */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="lg:col-span-2"
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 h-full">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2.5">
              <Award className="w-5 h-5 text-[#d45614]" />
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Store Health Score</h2>
            </div>
            <div className="p-6 flex flex-col items-center">
              <CircularProgress score={healthScore} size={160} />
              <p className="text-sm font-semibold text-gray-600 mt-4 mb-5">
                {healthScore >= 75 ? 'Excellent! Your store is thriving.' : healthScore >= 50 ? 'Good progress, room to improve.' : 'Needs attention. Follow the tips below.'}
              </p>
              {/* Breakdown */}
              <div className="w-full space-y-3">
                {healthBreakdown.map((item, i) => {
                  const pct = Math.round((item.score / item.max) * 100);
                  const StatusIcon = resolveIcon(item.icon);
                  const barColor = pct >= 80 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
                  const statusColor = pct >= 80 ? 'text-emerald-500' : pct >= 50 ? 'text-amber-500' : 'text-red-500';
                  return (
                    <div key={i} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`w-3.5 h-3.5 ${statusColor}`} />
                          <span className="text-xs font-semibold text-gray-600">{item.label}</span>
                        </div>
                        <span className="text-xs font-bold text-gray-400 tabular-nums">{item.score}/{item.max}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: barColor }}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.8, delay: i * 0.1 }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Weekly Comparison — 3 cols */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="lg:col-span-3"
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 h-full">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2.5">
              <BarChart3 className="w-5 h-5 text-[#d45614]" />
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Period Comparison</h2>
              <span className="text-xs text-gray-300 font-medium ml-auto">Last {days} days vs previous</span>
            </div>
            <div className="p-6">
              {weeklyComparison.length > 0 ? (
                <div className="space-y-4">
                  {/* Header row */}
                  <div className="grid grid-cols-12 text-xs font-bold text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-100">
                    <div className="col-span-3">Metric</div>
                    <div className="col-span-3 text-right">This Period</div>
                    <div className="col-span-3 text-right">Last Period</div>
                    <div className="col-span-3 text-right">Change</div>
                  </div>
                  {weeklyComparison.map((row, i) => {
                    const growth = row.growth || 0;
                    const isUp = growth > 0;
                    const isDown = growth < 0;
                    const changeColor = isUp ? 'text-emerald-600 bg-emerald-50' : isDown ? 'text-red-600 bg-red-50' : 'text-gray-400 bg-gray-50';
                    const formatVal = (v) => row.isCurrency ? fmt(v) : Number(v || 0).toLocaleString('en-IN');
                    return (
                      <motion.div
                        key={row.label}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08 + 0.3 }}
                        className="grid grid-cols-12 items-center py-3.5 hover:bg-gray-50/50 -mx-3 px-3 rounded-xl transition-colors"
                      >
                        <div className="col-span-3">
                          <span className="text-sm font-bold text-gray-800">{row.label}</span>
                        </div>
                        <div className="col-span-3 text-right">
                          <span className="text-sm font-extrabold text-gray-900 tabular-nums">{formatVal(row.current)}</span>
                        </div>
                        <div className="col-span-3 text-right">
                          <span className="text-sm font-semibold text-gray-300 tabular-nums">{formatVal(row.previous)}</span>
                        </div>
                        <div className="col-span-3 flex justify-end">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${changeColor}`}>
                            {isUp && <ArrowUpRight className="w-3 h-3" />}
                            {isDown && <ArrowDownRight className="w-3 h-3" />}
                            {!isUp && !isDown && <span className="w-3 h-3 flex items-center justify-center">--</span>}
                            {growth !== 0 ? `${isUp ? '+' : ''}${growth}%` : 'Same'}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState text="Not enough data for comparison yet. Keep running your store!" icon={<BarChart3 className="w-12 h-12" />} />
              )}

              {/* V4: Revenue Forecast */}
              {(() => {
                const revenueRow = weeklyComparison.find(r => r.label === 'Revenue');
                const weeklyRevenue = revenueRow?.current || 0;
                const revenueGrowth = revenueRow?.growth || 0;
                const forecast = calculateRevenueForecast(weeklyRevenue / (days / 7), revenueGrowth);
                if (!forecast || forecast.currentMonthly <= 0) return null;
                return (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-4 h-4 text-[#d45614]" />
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Revenue Forecast</h3>
                    </div>
                    <div className="space-y-3">
                      {/* Current trajectory */}
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <p className="text-xs text-gray-500 mb-2">
                          At your current growth rate ({revenueGrowth > 0 ? '+' : ''}{revenueGrowth}%/period):
                        </p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-400">Next month</p>
                            <p className="text-sm font-bold text-gray-900 tabular-nums">~{fmt(forecast.currentMonthly)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">In 3 months</p>
                            <p className="text-sm font-bold text-gray-900 tabular-nums">~{fmt(forecast.current3Month)}</p>
                          </div>
                        </div>
                      </div>
                      {/* Optimistic trajectory */}
                      <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                        <p className="text-xs text-emerald-700 mb-2">
                          If you fix cart abandonment + increase AOV:
                        </p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-emerald-600">Next month</p>
                            <p className="text-sm font-bold text-emerald-800 tabular-nums">~{fmt(forecast.optimisticMonthly)} <span className="text-xs font-semibold text-emerald-600">(+50%)</span></p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-emerald-600">In 3 months</p>
                            <p className="text-sm font-bold text-emerald-800 tabular-nums">~{fmt(forecast.optimistic3Month)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </motion.div>
      </div>

      {/* V3: Progress Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="mb-7"
      >
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2.5">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Progress Timeline</h2>
          </div>
          <div className="p-6">
            {progressData && progressData.weeks && progressData.weeks.length >= 2 ? (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-100 z-0" />
                <div className="flex items-start justify-between relative z-10">
                  {progressData.weeks.map((week, i) => {
                    const isCurrentWeek = i === progressData.weeks.length - 1;
                    const scoreColor = week.score >= 75 ? '#10B981' : week.score >= 50 ? '#F59E0B' : '#EF4444';
                    return (
                      <div key={i} className="flex flex-col items-center text-center flex-1">
                        {/* Score bubble */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: i * 0.1 + 0.3 }}
                          className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-extrabold text-white shadow-md mb-2 ${
                            isCurrentWeek ? 'ring-4 ring-offset-2' : ''
                          }`}
                          style={{
                            backgroundColor: scoreColor,
                            ringColor: isCurrentWeek ? scoreColor + '40' : 'transparent',
                          }}
                        >
                          {week.score}
                        </motion.div>
                        {/* Week label */}
                        <p className={`text-xs font-bold mb-0.5 ${isCurrentWeek ? 'text-gray-900' : 'text-gray-500'}`}>
                          {isCurrentWeek ? 'This Week' : `Week ${i + 1}`}
                        </p>
                        {/* Achievement label */}
                        <p className="text-[10px] text-gray-400 max-w-[80px] leading-tight">
                          {week.label || (week.highlight || '')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-6 h-6 text-amber-400" />
                </div>
                <p className="text-sm font-semibold text-gray-600 mb-1">Keep going!</p>
                <p className="text-xs text-gray-400 max-w-xs mx-auto leading-relaxed">
                  Your progress history will appear here after your first week. Check back soon to see how you're improving.
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Personalized Tips (V3 Money Translation + V4 Auto-Actions) */}
      {allTips.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="mb-7"
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Personalized Tips</h2>
                <span className="text-xs text-gray-300 font-medium">{allTips.length} tips</span>
              </div>
              {surveyAnswers && (
                <span className="text-xs text-gray-300 font-medium flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-400" />
                  Tailored for you
                </span>
              )}
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AnimatePresence>
                  {visibleTips.map((tip, i) => {
                    const Icon = resolveIcon(tip.icon);
                    const catConfig = tipCategoryConfig[tip.category] || tipCategoryConfig.growth;
                    const tipId = tip.id || tip.title?.toLowerCase().replace(/\s+/g, '_') || '';

                    // V4: Determine auto-action for this tip
                    const hasCouponAction = tipId.includes('coupon') || tip.title?.toLowerCase().includes('coupon');
                    const hasShareAction = tipId.includes('instagram') || tipId.includes('share') || tip.title?.toLowerCase().includes('instagram') || tip.title?.toLowerCase().includes('share');
                    const hasBundleAction = tipId.includes('bundle') || tip.title?.toLowerCase().includes('bundle');
                    const hasAutoAction = hasCouponAction || hasShareAction || hasBundleAction;

                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: i * 0.05 }}
                        className={`group relative overflow-hidden p-4 ${catConfig.bg} border ${catConfig.border} rounded-2xl hover:shadow-md transition-all duration-200`}
                      >
                        <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/30 rounded-full" />
                        <div className="relative">
                          <div className="flex items-start gap-3 mb-2.5">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: catConfig.color + '18' }}>
                              <Icon className="w-4 h-4" style={{ color: catConfig.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h4 className="text-sm font-bold text-gray-900 truncate">{tip.title}</h4>
                              </div>
                              <p className="text-xs text-gray-500 leading-relaxed">{tip.description}</p>
                              {/* V3: Benchmark line */}
                              {tip.benchmark && (
                                <p className="text-[10px] text-gray-400 mt-1 italic">Platform avg: {tip.benchmark}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: catConfig.color + '15', color: catConfig.color }}>
                                {tip.metric}
                              </span>
                              {/* V3: Money impact badge */}
                              {tip.money_impact && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                  <IndianRupee className="w-2.5 h-2.5" />
                                  {tip.money_impact}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* V4: Auto-action buttons */}
                              {hasCouponAction && (
                                <button
                                  onClick={() => navigate('/my-coupons?auto=WELCOME10&discount=10')}
                                  className="text-[10px] font-bold flex items-center gap-1 px-2 py-1 bg-[#d45614]/10 text-[#d45614] rounded-lg hover:bg-[#d45614]/20 transition-all active:scale-95"
                                >
                                  <Zap className="w-3 h-3" />
                                  Auto-create WELCOME10
                                </button>
                              )}
                              {hasShareAction && !hasCouponAction && (
                                <button
                                  onClick={() => {
                                    const caption = `\u2728 New arrivals at our store! Use code WELCOME10 for 10% off. Shop now! #fashion #ethnic #indianwear`;
                                    handleCopyCaption(caption);
                                  }}
                                  className="text-[10px] font-bold flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all active:scale-95"
                                >
                                  {copiedCaption ? <><ClipboardCheck className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Generate caption</>}
                                </button>
                              )}
                              {hasBundleAction && !hasCouponAction && !hasShareAction && (
                                <button
                                  onClick={() => navigate('/collections-manager')}
                                  className="text-[10px] font-bold flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-all active:scale-95"
                                >
                                  <Package className="w-3 h-3" />
                                  Create bundle
                                </button>
                              )}
                              {/* Original CTA button (show only if no auto-action) */}
                              {tip.cta && tip.route && !hasAutoAction && (
                                <button
                                  onClick={() => navigate(tip.route)}
                                  className="text-xs font-bold flex items-center gap-1 hover:gap-2 transition-all duration-200"
                                  style={{ color: catConfig.color }}
                                >
                                  {tip.cta}
                                  <ArrowRight className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
              {allTips.length > 8 && (
                <div className="flex justify-center mt-5">
                  <button
                    onClick={() => setShowAllTips(!showAllTips)}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:text-gray-700 transition-all active:scale-95"
                  >
                    {showAllTips ? 'Show less' : `Show all ${allTips.length} tips`}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showAllTips ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* V4: Seasonal Intelligence */}
      {(() => {
        const upcomingFestivals = getUpcomingFestivals(60, 2);
        if (upcomingFestivals.length === 0) return null;
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mb-7"
          >
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
              <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2.5">
                <Calendar className="w-5 h-5 text-[#d45614]" />
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Coming Up</h2>
              </div>
              <div className="p-5">
                <div className="space-y-4">
                  {upcomingFestivals.map((fest, i) => (
                    <motion.div
                      key={fest.name}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 + 0.2 }}
                      className="relative overflow-hidden p-5 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border border-orange-100 rounded-2xl"
                    >
                      <div className="absolute -top-6 -right-6 w-24 h-24 bg-orange-200/10 rounded-full" />
                      <div className="relative">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl flex-shrink-0">{fest.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className="text-sm font-bold text-gray-900">{fest.name}</h4>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#d45614]/10 text-[#d45614]">
                                {fest.daysUntil === 1 ? 'Tomorrow' : `in ${fest.daysUntil} days`}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mb-2">{fest.desc}</p>
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-[#d45614]">
                              <ArrowRight className="w-3 h-3" />
                              {fest.action}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        );
      })()}

      {/* Section 4: Retake Survey */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center justify-center py-4"
      >
        <button
          onClick={handleRetakeSurvey}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#d45614] font-medium transition-colors duration-200"
        >
          <RotateCcw className="w-4 h-4" />
          {surveyAnswers ? 'Retake business consultation' : 'Take the business consultation'}
        </button>
      </motion.div>
    </>
  );
};

// ── Main Analytics Component ────────────────────────────────────────────────
const Analytics = () => {
  const { authToken } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [days, setDays] = useState(30);

  // Overview
  const [overview, setOverview] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Funnel
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  // Comparison & Alerts
  const [comparison, setComparison] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  // Traffic
  const [traffic, setTraffic] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [devices, setDevices] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);

  // Products
  const [topPages, setTopPages] = useState(null);
  const [categories, setCategories] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);

  // Customers
  const [customerValue, setCustomerValue] = useState(null);

  const authAxios = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: { Authorization: `Bearer ${authToken?.access}` },
  }), [authToken?.access]);

  // Fetch overview on mount
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const [overviewRes, topRes, statusRes] = await Promise.all([
          authAxios.get('/analytics/overview/'),
          authAxios.get('/analytics/top_products/'),
          authAxios.get('/analytics/orders_by_status/'),
        ]);
        setOverview(overviewRes.data);
        setTopProducts(topRes.data.products || []);
        setStatusData(statusRes.data);
      } catch (err) {
        devLog.error('Analytics fetch error:', err);
      } finally {
        setOverviewLoading(false);
      }
    };
    fetchOverview();
  }, [authAxios]);

  // Fetch comparison + alerts when overview or insights tab active, or days change
  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'insights') {
      Promise.all([
        authAxios.get('/analytics/comparison/', { params: { days } }),
        authAxios.get('/analytics/alerts/'),
      ]).then(([compRes, alertRes]) => {
        setComparison(compRes.data);
        setAlerts(alertRes.data.alerts || []);
      }).catch(err => devLog.error('Comparison/alerts fetch error:', err));
    }
  }, [activeTab, days, authAxios]);

  // Fetch funnel when overview tab active or days change
  useEffect(() => {
    if (activeTab === 'overview') {
      setFunnelLoading(true);
      authAxios.get('/analytics/funnel/', { params: { days } })
        .then(res => setFunnel(res.data.funnel || []))
        .catch(err => devLog.error('Funnel fetch error:', err))
        .finally(() => setFunnelLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch traffic data when traffic tab active or days change
  useEffect(() => {
    if (activeTab === 'traffic') {
      setTrafficLoading(true);
      Promise.all([
        authAxios.get('/analytics/traffic/', { params: { days } }),
        authAxios.get('/analytics/referrers/', { params: { days } }),
        authAxios.get('/analytics/peak_hours/', { params: { days } }),
        authAxios.get('/analytics/devices/', { params: { days } }),
      ]).then(([trafficRes, refRes, peakRes, devRes]) => {
        setTraffic(trafficRes.data);
        setReferrers(refRes.data.referrers || []);
        setPeakHours(peakRes.data);
        setDevices(devRes.data.devices || []);
      }).catch(err => devLog.error('Traffic fetch error:', err))
        .finally(() => setTrafficLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch geography data when geography tab active
  useEffect(() => {
    if (activeTab === 'traffic' || activeTab === 'geography') {
      setGeoLoading(true);
      authAxios.get('/analytics/geography/', { params: { days } })
        .then(res => setGeoData(res.data))
        .catch(err => devLog.error('Geography fetch error:', err))
        .finally(() => setGeoLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch products data when products or insights tab active or days change
  useEffect(() => {
    if (activeTab === 'products' || activeTab === 'insights') {
      setProductsLoading(true);
      Promise.all([
        authAxios.get('/analytics/top_pages/', { params: { days } }),
        authAxios.get('/analytics/categories/', { params: { days } }),
        authAxios.get('/analytics/product_conversion/', { params: { days } }),
        authAxios.get('/analytics/abandoned_carts/'),
      ]).then(([topRes, catRes, convRes, cartRes]) => {
        setTopPages(topRes.data.products || []);
        setCategories(catRes.data.categories || []);
        setConversion(convRes.data.products || []);
        setAbandonedCarts(cartRes.data);
      }).catch(err => devLog.error('Products fetch error:', err))
        .finally(() => setProductsLoading(false));
    }
  }, [activeTab, days, authAxios]);

  // Fetch customer value when customers or insights tab active
  useEffect(() => {
    if (activeTab === 'customers' || activeTab === 'insights') {
      authAxios.get('/analytics/customer_value/')
        .then(res => setCustomerValue(res.data))
        .catch(err => devLog.error('Customer value fetch error:', err));
    }
  }, [activeTab, authAxios]);

  const handleDismissAlert = useCallback((index) => {
    setDismissedAlerts(prev => new Set([...prev, index]));
  }, []);

  return (
    <Base
      Content={
        <div className="min-h-screen bg-gray-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
            {/* Header + Day Selector */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d45614] to-[#e8722e] flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-200">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 font-fell tracking-tight">Analytics</h1>
                  <p className="text-sm text-gray-400 mt-0.5">Track performance, discover insights</p>
                </div>
              </div>
              <div className="flex gap-1 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm">
                {DAY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setDays(opt.value)}
                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-200 ${
                      days === opt.value
                        ? 'bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white shadow-md shadow-orange-200'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tabs — Premium pill navigation */}
            <div className="flex gap-1.5 mb-8 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm overflow-x-auto scrollbar-hide">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-lg transition-all duration-200 whitespace-nowrap ${
                      isActive
                        ? 'bg-gradient-to-r from-[#d45614] to-[#e8722e] text-white shadow-md shadow-orange-200'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content with smooth appearance */}
            <div className="animate-fadeIn">
              {activeTab === 'overview' && (
                overviewLoading ? <LoadingState text="Loading analytics..." />
                  : <OverviewTab overview={overview} topProducts={topProducts} statusData={statusData} funnel={funnel} funnelLoading={funnelLoading} comparison={comparison} alerts={alerts} dismissedAlerts={dismissedAlerts} onDismissAlert={handleDismissAlert} />
              )}
              {activeTab === 'traffic' && <TrafficTab traffic={traffic} referrers={referrers} peakHours={peakHours} devices={devices} loading={trafficLoading} geoData={geoData} geoLoading={geoLoading} />}
              {activeTab === 'products' && <ProductsTab topPages={topPages} categories={categories} conversion={conversion} abandonedCarts={abandonedCarts} loading={productsLoading} />}
              {activeTab === 'customers' && <CustomersTab authAxios={authAxios} customerValue={customerValue} />}
              {activeTab === 'insights' && <InsightsTab authAxios={authAxios} alerts={alerts} comparison={comparison} conversion={conversion} abandonedCarts={abandonedCarts} customerValue={customerValue} days={days} />}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default Analytics;
